"use client";

import {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import {
  Search,
  Filter,
  Grid,
  List,
  Plus,
  ChevronRight,
  ChevronLeft,
  Briefcase,
  Building2,
  Users,
  Edit,
  Eye,
  Trash2,
  Brain,
  Check,
  X,
  MessageCircle,
  Linkedin,
  Mail,
  Copy,
  Sparkles,
  Link,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Info,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./job-roles-page.css";
import BackButton from "../shared/backButton";
import Pagination from "../shared/paginator/paginator";
import Modal from "../layout/Modal";
import MessageManager from "../layout/Message";
import {
  getAllJobRolesApi,
  createJobRoleApi,
  aiJobDescriptionGenerationApi,
  editJobRoleApi,
  getJobRoleById,
  deleteJobRole,
  getAllPeopleApi,
  getPeopleSkillValuesApi,
  shareJobRolesWithPartnersApi,
} from "../../services/jobRolesService";
import {
  handleApiError as baseHandleApiError,
  formatEmployeeCreatedAt,
} from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import {
  getDepartmentsListApi,
  getAiOrganizationSuggestedRole,
  saveAiGeneratedOrganizationRoles,
} from "../../services/settingsService"; // Added import for dynamic departments
import BulkAction from "../shared/utils/bulkAction";
import EmptyState from "../shared/EmptyState";
import FilterNoResults from "../shared/FilterNoResults";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getJobRolesAccess,
  getLoggedInUserRole,
  ROLES,
} from "../shared/access";
import { getPartnersListApi } from "../../services/hiringService";
import {
  readPartnerCollaborationState,
  subscribePartnerCollaborationState,
} from "../shared/utils/partnerCollaborationState";

const DUMMY_BULK_SHARE_PARTNERS = [
  {
    id: "partner-apex",
    name: "Apex Talent Partners",
    orgCode: "ATP-4821",
    admin: "Mira Kapoor",
    email: "mira@apex-demo.com",
    status: "ACTIVE",
    profileShare: true,
    jobRolesShare: true,
  },
  {
    id: "partner-globalstaff",
    name: "GlobalStaff Solutions",
    orgCode: "GSS-7184",
    admin: "Daniel Brooks",
    email: "daniel@globalstaff-demo.com",
    status: "ACTIVE",
    profileShare: true,
    jobRolesShare: true,
  },
  {
    id: "partner-northbridge",
    name: "NorthBridge Talent",
    orgCode: "NBT-2056",
    admin: "Aarav Malhotra",
    email: "aarav@northbridge-demo.com",
    status: "ACTIVE",
    profileShare: true,
    jobRolesShare: true,
  },
];
// Define SuccessNotificationModal directly in this file
const SuccessNotificationModal = ({ show, message, onClose }) => {
  const { t } = useTranslation();
  if (!show) return null;

  return (
    <div className="job-role-modal-overlay">
      <div className="job-role-modal success-notification-modal">
        <div className="job-role-modal-header">
          <h2>{t("common.success")}!</h2>
          <button className="job-role-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="job-role-modal-content" style={{ textAlign: "center" }}>
          <CheckCircle
            size={48}
            color="#10B981"
            style={{ marginBottom: "20px" }}
          />
          <p style={{ fontSize: "18px", color: "var(--text-primary)" }}>
            {message}
          </p>
        </div>
        <div
          className="job-role-modal-footer"
          style={{ justifyContent: "center" }}
        >
          <button className="btn btn-primary" onClick={onClose}>
            {t("common.ok")}
          </button>
        </div>
      </div>
    </div>
  );
};

const SHORT_DESCRIPTION_MAX_LENGTH = 120;
const limitShortDescription = (value = "") =>
  value.slice(0, SHORT_DESCRIPTION_MAX_LENGTH);

const JobRoleModalTooltip = ({ tooltip, children, id }) => {
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({
    top: -9999,
    left: -9999,
    arrowLeft: 24,
    placement: "top",
    ready: false,
  });

  const updateTooltipPosition = useCallback(() => {
    const triggerElement = triggerRef.current;
    const tooltipElement = tooltipRef.current;

    if (!triggerElement || !tooltipElement) return;

    const triggerRect = triggerElement.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();

    const viewportPadding = 12;
    const tooltipGap = 10;

    const availableSpaceAbove = triggerRect.top - viewportPadding;
    const availableSpaceBelow =
      window.innerHeight - triggerRect.bottom - viewportPadding;

    const shouldShowAbove =
      availableSpaceAbove >= tooltipRect.height + tooltipGap ||
      availableSpaceAbove >= availableSpaceBelow;

    const placement = shouldShowAbove ? "top" : "bottom";

    let top = shouldShowAbove
      ? triggerRect.top - tooltipRect.height - tooltipGap
      : triggerRect.bottom + tooltipGap;

    top = Math.max(
      viewportPadding,
      Math.min(top, window.innerHeight - tooltipRect.height - viewportPadding),
    );

    const triggerCenter = triggerRect.left + triggerRect.width / 2;

    let left = triggerCenter - tooltipRect.width / 2;

    left = Math.max(
      viewportPadding,
      Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding),
    );

    const arrowLeft = Math.max(
      18,
      Math.min(tooltipRect.width - 18, triggerCenter - left),
    );

    setPosition({
      top,
      left,
      arrowLeft,
      placement,
      ready: true,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) return undefined;

    const animationFrame = window.requestAnimationFrame(updateTooltipPosition);

    window.addEventListener("resize", updateTooltipPosition);
    window.addEventListener("scroll", updateTooltipPosition, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateTooltipPosition);
      window.removeEventListener("scroll", updateTooltipPosition, true);
    };
  }, [isVisible, updateTooltipPosition]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const closeIfOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        tooltipRef.current?.contains(event.target)
      ) {
        return;
      }

      setIsVisible(false);
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsVisible(false);
      }
    };

    document.addEventListener("pointerdown", closeIfOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeIfOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isVisible]);

  return (
    <>
      <span
        ref={triggerRef}
        className="jr-modal-tooltip-trigger"
        role="button"
        tabIndex={0}
        aria-describedby={id}
        aria-expanded={isVisible}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsVisible((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            setIsVisible((current) => !current);
          }

          if (event.key === "Escape") {
            setIsVisible(false);
          }
        }}
        onMouseEnter={() => {
          const supportsHover =
            typeof window !== "undefined" &&
            window.matchMedia?.("(hover: hover)")?.matches;

          if (supportsHover) {
            setIsVisible(true);
          }
        }}
        onMouseLeave={() => {
          const supportsHover =
            typeof window !== "undefined" &&
            window.matchMedia?.("(hover: hover)")?.matches;

          if (supportsHover) {
            setIsVisible(false);
          }
        }}
        onFocus={(event) => {
          if (event.currentTarget.matches(":focus-visible")) {
            setIsVisible(true);
          }
        }}
        onBlur={() => setIsVisible(false)}
      >
        {children}
      </span>

      {isVisible &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tooltipRef}
            id={id}
            role="tooltip"
            className={`custom-tooltip jr-modal-portal-tooltip ${
              position.placement
            } ${position.ready ? "is-ready" : ""}`}
            style={{
              "--jr-tooltip-top": `${position.top}px`,
              "--jr-tooltip-left": `${position.left}px`,
              "--jr-tooltip-arrow-left": `${position.arrowLeft}px`,
            }}
          >
            <div className="tooltip-header">
              <strong>{tooltip.title}</strong>
            </div>

            <div className="tooltip-message">{tooltip.message}</div>

            {tooltip.details && (
              <div className="tooltip-details">{tooltip.details}</div>
            )}

            {tooltip.action && (
              <div className="tooltip-action">{tooltip.action}</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};
const JobRolesPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("All");
  const [filterLevel, setFilterLevel] = useState("All");
  const [filterHiringStatus, setFilterHiringStatus] = useState("All");
  const [viewMode, setViewMode] = useState("list"); // "card" or "list"
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createMode, setCreateMode] = useState(""); // "new" or "employee"
  const [createStep, setCreateStep] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState({
    show: false,
    roleId: null, // This is the _id from the frontend
  });
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedRoles, setSelectedRoles] = useState([]); // These are _id's
  const [showShareModal, setShowShareModal] = useState({
    show: false,
    role: null,
  });
  const [descriptionRole, setDescriptionRole] = useState(null);

  // Controls the expanded description in table/list view.
  // Only one row will remain expanded at a time.
  const [expandedDescriptionRoleId, setExpandedDescriptionRoleId] =
    useState(null);
  // const [showAISuggestedRolesModal, setShowAISuggestedRolesModal] =
  //   useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkSharePartnerModalOpen, setBulkSharePartnerModalOpen] =
    useState(false);
  const [bulkSharePartnerSelection, setBulkSharePartnerSelection] =
    useState("");
  const bulkSharePartnerPickerRef = useRef(null);

  const [bulkSharePartnerSearch, setBulkSharePartnerSearch] = useState("");

  const [showBulkSharePartnerResults, setShowBulkSharePartnerResults] =
    useState(false);
  const [copyButtonText, setCopyButtonText] = useState("Copy Link");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [jobRolesLength, setJobRolesLength] = useState(0);
  const [editingRole, setEditingRole] = useState(null);
  const [skillInput, setSkillInput] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState({
    show: false,
    message: "",
  });
  const [successModal, setSuccessModal] = useState({
    show: false,
    title: "",
    message: "",
  });
  const roleAccess = getJobRolesAccess();
  const loggedInRole = getLoggedInUserRole();
  const isGuest = loggedInRole === ROLES.GUEST;
  const permissionDeniedMessage = t("common.permissions.contactAdminForAction");
  const showDisabledCreateRoleTooltip = isGuest && !roleAccess.canAddRole;
  const showDisabledEditRoleTooltip = isGuest && !roleAccess.canEditRole;
  const showDisabledDeleteRoleTooltip = isGuest && !roleAccess.canDeleteRole;

  const ROLE_AVAILABILITY = {
    ALL: "All",
    AVAILABLE: "Available",
    CLOSED: "Closed",
  };

  const ROLE_ACCESS = {
    ALL: "All",
    PUBLIC: "Public",
    PRIVATE: "Private",
  };

  const roleAvailabilityOptions = [
    { value: ROLE_AVAILABILITY.ALL, label: "All" },
    { value: ROLE_AVAILABILITY.AVAILABLE, label: "Available" },
    { value: ROLE_AVAILABILITY.CLOSED, label: "Closed" },
  ];

  const getRoleAvailabilityDisplayLabel = (status) =>
    status === ROLE_AVAILABILITY.AVAILABLE ? "Open" : status;
  const roleAccessOptions = [
    { value: ROLE_ACCESS.ALL, label: "All" },
    { value: ROLE_ACCESS.PUBLIC, label: "Public" },
    { value: ROLE_ACCESS.PRIVATE, label: "Private" },
  ];

  const [filterRoleAccess, setFilterRoleAccess] = useState(ROLE_ACCESS.ALL);
  const [newRolePartnerSearch, setNewRolePartnerSearch] = useState("");
  const [editRolePartnerSearch, setEditRolePartnerSearch] = useState("");
  const [partnerSharingSettings, setPartnerSharingSettings] = useState({
    hasActivePartnerConnections: false,
    jobRoleSharingEnabled: false,
    activePartners: [],
    allPartners: [],
  });
  const [partnerCollaboration, setPartnerCollaboration] = useState(
    readPartnerCollaborationState,
  );

  useEffect(() => {
    return subscribePartnerCollaborationState(setPartnerCollaboration);
  }, []);
  const [showCloseSharedConfirm, setShowCloseSharedConfirm] = useState({
    show: false,
    mode: "",
  });
  // New states for inline form errors
  const [roleNameError, setRoleNameError] = useState("");
  const [departmentError, setDepartmentError] = useState("");
  const [levelError, setLevelError] = useState("");
  const [shortDescriptionError, setShortDescriptionError] = useState("");

  // Job roles data from API
  const [jobRoles, setJobRoles] = useState([]);

  // New states for employee-based creation
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingSkillValues, setIsLoadingSkillValues] = useState(false);

  // State for dynamic departments dropdown
  const [departments, setDepartments] = useState([]); // Added state for dynamic departments

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const normalizeRoleAvailability = (value, availability = true) => {
    /*
     * The latest API uses:
     * availability: true  -> Available
     * availability: false -> Closed
     */
    if (typeof availability === "boolean") {
      return availability
        ? ROLE_AVAILABILITY.AVAILABLE
        : ROLE_AVAILABILITY.CLOSED;
    }

    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase();

    if (["closed", "close"].includes(normalizedValue)) {
      return ROLE_AVAILABILITY.CLOSED;
    }

    return ROLE_AVAILABILITY.AVAILABLE;
  };

  const getRoleAvailability = (role = {}) => {
    // Give the latest Boolean API field first priority.
    if (typeof role.availability === "boolean") {
      return role.availability
        ? ROLE_AVAILABILITY.AVAILABLE
        : ROLE_AVAILABILITY.CLOSED;
    }

    return normalizeRoleAvailability(
      role.roleAvailability || role.hiringStatus,
      role.openForHiring ?? role.isOpenForHiring,
    );
  };

  // const isRoleAvailable = (role) =>
  //   getRoleAvailability(role) === ROLE_AVAILABILITY.AVAILABLE;
  // const getRoleAvailability = (role) =>
  //   normalizeRoleAvailability(
  //     role.roleAvailability || role.hiringStatus,
  //     role.openForHiring,
  //   );
  const isRoleAvailable = (role) =>
    getRoleAvailability(role) === ROLE_AVAILABILITY.AVAILABLE;
  const getRoleAccess = (role) => {
    const rawAccess = role.roleAccess || role.access || role.visibility;
    const normalizedAccess = String(rawAccess || "")
      .trim()
      .toLowerCase();
    const hasSelectedPartners =
      (Array.isArray(role.sharedPartnerIds) &&
        role.sharedPartnerIds.length > 0) ||
      (Array.isArray(role.sharedWithPartners) &&
        role.sharedWithPartners.length > 0);

    if (
      hasSelectedPartners ||
      normalizedAccess.includes("public") ||
      normalizedAccess.includes("shared")
    ) {
      return ROLE_ACCESS.PUBLIC;
    }

    return ROLE_ACCESS.PRIVATE;
  };

  const hasRolePartnerSharing = (role) =>
    getRoleAccess(role) === ROLE_ACCESS.PUBLIC &&
    ((Array.isArray(role.sharedPartnerIds) &&
      role.sharedPartnerIds.length > 0) ||
      (Array.isArray(role.sharedWithPartners) &&
        role.sharedWithPartners.length > 0));

  const getBulkShareRoleBuckets = (roleIds = selectedRoles) =>
    (Array.isArray(roleIds) ? roleIds : []).reduce(
      (buckets, roleId) => {
        const role = jobRoles.find((jobRole) => jobRole.id === roleId);

        if (!role) {
          return buckets;
        }

        if (getRoleAccess(role) === ROLE_ACCESS.PUBLIC) {
          buckets.publicRoles.push(role);
        } else {
          buckets.privateRoles.push(role);
        }

        return buckets;
      },
      {
        publicRoles: [],
        privateRoles: [],
      },
    );

  const getBulkSharePrivateRoleMessage = (privateRoleCount) =>
    `${privateRoleCount} private job role${
      privateRoleCount === 1 ? "" : "s"
    } skipped. Only public roles can be shared with partners.`;

  const getRoleStatusClass = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/\s+/g, "-");

  // const canUsePartnerRoleSharing =
  //   partnerCollaboration.enabled &&
  //   partnerCollaboration.jobRoleSharingEnabled &&
  //   partnerSharingSettings.hasActivePartnerConnections &&
  //   partnerSharingSettings.jobRoleSharingEnabled;

  // The latest getPartnersList API is the source of truth.
  // Enable Public when at least one active partner supports Job Roles.
  const canUsePartnerRoleSharing =
    partnerSharingSettings.hasActivePartnerConnections &&
    partnerSharingSettings.jobRoleSharingEnabled;

  const hasPendingPartnerConnections = Array.isArray(
    partnerSharingSettings.allPartners,
  )
    ? partnerSharingSettings.allPartners.some(
        (partner) =>
          String(partner.status || "")
            .trim()
            .toUpperCase() === "PENDING",
      )
    : false;

  useEffect(() => {
    if (canUsePartnerRoleSharing) return;

    setNewRole((previousRole) => ({
      ...previousRole,
      visibility: ROLE_ACCESS.PRIVATE,
      roleAccess: ROLE_ACCESS.PRIVATE,
      selectedPartnerIds: [],
    }));

    setEditRole((previousRole) => ({
      ...previousRole,
      visibility: ROLE_ACCESS.PRIVATE,
      roleAccess: ROLE_ACCESS.PRIVATE,
      selectedPartnerIds: [],
    }));
  }, [canUsePartnerRoleSharing]);
  const shouldShowPartnerShareToast = (roleState) =>
    canUsePartnerRoleSharing &&
    getRoleAvailability(roleState) !== ROLE_AVAILABILITY.CLOSED &&
    roleState.roleAccess === ROLE_ACCESS.PUBLIC &&
    (roleState.selectedPartnerIds || roleState.sharedPartnerIds || []).length >
      0;

  const showRoleSaveSuccess = (primaryMessage, roleState) => {
    MessageManager.showToast(
      "success",
      shouldShowPartnerShareToast(roleState)
        ? "Changes saved"
        : primaryMessage,
    );
  };

  // Form state for creating a new job role
  const [newRole, setNewRole] = useState({
    roleName: "",
    department: "",
    level: "Executive or Senior Management",
    shortDescription: "",
    fullDescription: "",
    visibility: "Public",
    openForHiring: true,
    hiringStatus: "Available",
    roleAvailability: "Available",
    roleAccess: ROLE_ACCESS.PRIVATE,
    selectedPartnerIds: [],
    createdFrom: "Scratch",
    peopleUsedForCreation: [],
    skillValues: {
      adaptability: "5.0",
      communication: "5.0",
      creativity: "5.0",
      attentionToDetail: "5.0",
      problemSolving: "5.0",
      teamWork: "5.0",
      leadership: "5.0",
      timeManagement: "5.0",
      interpersonalSkills: "5.0",
      workEthic: "5.0",
      professionValue: "5.0",
      purposeValue: "5.0",
      spiritValue: "5.0",
      rewardValue: "5.0",
    },
  });

  // Edit form state
  const [editRole, setEditRole] = useState({
    jobId: "",
    roleName: "",
    department: "",
    level: "",
    shortDescription: "",
    fullDescription: "",

    availability: true,
    openForHiring: true,
    hiringStatus: ROLE_AVAILABILITY.AVAILABLE,
    roleAvailability: ROLE_AVAILABILITY.AVAILABLE,

    visibility: ROLE_ACCESS.PRIVATE,
    roleAccess: ROLE_ACCESS.PRIVATE,
    selectedPartnerIds: [],

    education: [],
    hardSkills: [],
    majors: [],
    certifications: [],
    skillValues: {},
  });
  // State for employee selection in employee-based role creation
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const levels = [
    {
      value: "Executive or Senior Management",
      label: t("jobRoles.levelOptions.executiveOrSeniorManagement"),
    },
    {
      value: "Middle Management",
      label: t("jobRoles.levelOptions.middleManagement"),
    },
    {
      value: "First-Level Management",
      label: t("jobRoles.levelOptions.firstLevelManagement"),
    },
    {
      value: "Intermediate or Experienced (Senior Staff)",
      label: t("jobRoles.levelOptions.intermediateOrExperiencedSeniorStaff"),
    },
    {
      value: "Entry-Level",
      label: t("jobRoles.levelOptions.entryLevel"),
    },
  ];

  // Load job roles on component mount and when filters change
  useEffect(() => {
    handleFilterData();
  }, [
    currentPage,
    searchTerm,
    filterDepartment,
    filterLevel,
    filterHiringStatus,
    filterRoleAccess,
    sortBy,
    sortOrder,
    itemsPerPage,
  ]);
  const [selectedJobRoles, setSelectedJobRoles] = useState([]);
  const [aiRoleDepartmentMap, setAiRoleDepartmentMap] = useState({});
  const [aiRoleDepartmentSearchMap, setAiRoleDepartmentSearchMap] = useState(
    {},
  );
  const [aiRoleDepartmentOptions, setAiDepartmentList] = useState([]);
  // const aiRoleDepartmentOptions = [
  //   { id: "engineering", name: "Engineering" },
  //   { id: "product", name: "Product" },
  //   { id: "design", name: "Design" },
  //   { id: "sales", name: "Sales" },
  //   { id: "marketing", name: "Marketing" },
  //   { id: "customer-success", name: "Customer Success" },
  //   { id: "operations", name: "Operations" },
  //   { id: "finance", name: "Finance" },
  //   { id: "people", name: "People & Culture" },

  //   { id: "executive-leadership", name: "Executive Leadership" },
  //   { id: "business-development", name: "Business Development" },
  //   { id: "strategy", name: "Strategy" },
  //   { id: "legal", name: "Legal" },
  //   { id: "compliance", name: "Compliance" },
  //   { id: "security", name: "Security" },
  //   { id: "information-technology", name: "Information Technology" },
  //   { id: "data-analytics", name: "Data & Analytics" },
  //   { id: "research", name: "Research" },
  //   { id: "machine-learning", name: "Machine Learning" },
  //   { id: "ai-research", name: "AI Research" },
  //   { id: "quality-assurance", name: "Quality Assurance" },
  //   { id: "devops", name: "DevOps" },
  //   { id: "infrastructure", name: "Infrastructure" },
  //   { id: "platform", name: "Platform" },
  //   { id: "developer-relations", name: "Developer Relations" },
  //   { id: "technical-support", name: "Technical Support" },
  //   { id: "customer-experience", name: "Customer Experience" },
  //   { id: "account-management", name: "Account Management" },
  //   { id: "partnerships", name: "Partnerships" },
  //   { id: "growth", name: "Growth" },
  //   { id: "brand", name: "Brand" },
  //   { id: "communications", name: "Communications" },
  //   { id: "public-relations", name: "Public Relations" },
  //   { id: "content", name: "Content" },
  //   { id: "community", name: "Community" },
  //   { id: "creative", name: "Creative" },
  //   { id: "user-research", name: "User Research" },
  //   { id: "ux-research", name: "UX Research" },
  //   { id: "procurement", name: "Procurement" },
  //   { id: "supply-chain", name: "Supply Chain" },
  //   { id: "logistics", name: "Logistics" },
  //   { id: "facilities", name: "Facilities" },
  //   { id: "administration", name: "Administration" },
  //   { id: "learning-development", name: "Learning & Development" },
  //   { id: "recruiting", name: "Recruiting" },
  //   { id: "talent-acquisition", name: "Talent Acquisition" },
  //   { id: "employee-experience", name: "Employee Experience" },
  //   { id: "payroll", name: "Payroll" },
  //   { id: "investor-relations", name: "Investor Relations" },
  //   { id: "risk-management", name: "Risk Management" },
  //   { id: "audit", name: "Audit" },
  //   { id: "product-marketing", name: "Product Marketing" },
  //   { id: "sales-engineering", name: "Sales Engineering" },
  //   { id: "revenue-operations", name: "Revenue Operations" },
  //   { id: "business-operations", name: "Business Operations" },
  //   { id: "program-management", name: "Program Management" },
  //   { id: "project-management", name: "Project Management" },
  //   { id: "training", name: "Training" },
  //   { id: "documentation", name: "Documentation" },
  // ];

  const areAllSelectedAiRolesMapped =
    selectedJobRoles.length > 0 &&
    selectedJobRoles.every(
      (roleName) => (aiRoleDepartmentMap[roleName] || []).length > 0,
    );

  const handleJobRoleSelection = (jobRole) => {
    const isSelected = selectedJobRoles.some(
      (selected) => selected === jobRole,
    );

    if (isSelected) {
      setSelectedJobRoles((prev) =>
        prev.filter((selected) => selected !== jobRole),
      );

      setAiRoleDepartmentMap((prev) => {
        const updated = { ...prev };
        delete updated[jobRole];
        return updated;
      });

      setAiRoleDepartmentSearchMap((prev) => {
        const updated = { ...prev };
        delete updated[jobRole];
        return updated;
      });
    } else {
      setSelectedJobRoles((prev) => [...prev, jobRole]);

      setAiRoleDepartmentMap((prev) => ({
        ...prev,
        [jobRole]: prev[jobRole] || [],
      }));
    }
  };

  const handleAiRoleDepartmentToggle = (roleName, departmentName) => {
    setAiRoleDepartmentMap((prev) => {
      const currentDepartments = prev[roleName] || [];
      const isAlreadyMapped = currentDepartments.includes(departmentName);

      return {
        ...prev,
        [roleName]: isAlreadyMapped
          ? currentDepartments.filter((dept) => dept !== departmentName)
          : [...currentDepartments, departmentName],
      };
    });
  };

  const handleAiRoleDepartmentSearchChange = (roleName, value) => {
    setAiRoleDepartmentSearchMap((prev) => ({
      ...prev,
      [roleName]: value,
    }));
  };

  const getFilteredAiRoleDepartments = (roleName) => {
    const searchValue = (aiRoleDepartmentSearchMap[roleName] || "")
      .trim()
      .toLowerCase();

    if (!searchValue) return aiRoleDepartmentOptions;

    return aiRoleDepartmentOptions.filter((department) =>
      department.name.toLowerCase().includes(searchValue),
    );
  };
  // GET THE AI SUGGESTED ROLES WHEN MODAL IS OPENED
  const [aiSuggestedRoles, setAiSuggestedRoles] = useState([]);

  const [showFilters, setShowFilters] = useState(false);

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterDepartment("All");
    setFilterLevel("All");
    setFilterHiringStatus("All");
    setFilterRoleAccess(ROLE_ACCESS.ALL);
    setSortBy("name");
    setSortOrder("asc");
    setShowSearchBar(false);
    setShowFiltersPanel(false);
    setCurrentPage(1);
  };

  // useEffect(() => {
  //   if (showAISuggestedRolesModal) {
  //     getAiOrganizationSuggestedRoleApiHandler();
  //   }
  // }, [showAISuggestedRolesModal]);
  // Fetch AI suggestions when the Create modal is open and AI mode is selected
  // useEffect(() => {
  //   if (showCreateModal && createMode === "ai") {
  //     getAiOrganizationSuggestedRoleApiHandler();
  //   }
  // }, [showCreateModal, createMode]);

  // Fetch AI suggestions only when we're on the AI step screen
  useEffect(() => {
    if (showCreateModal && createMode === "ai" && createStep === 2) {
      getAiOrganizationSuggestedRoleApiHandler();
    }
  }, [showCreateModal, createMode, createStep]);
  const getAiOrganizationSuggestedRoleApiHandler = async () => {
    try {
      setIsLoading(true);
      const response = await getAiOrganizationSuggestedRole();
      setAiSuggestedRoles(response.data);
    } catch (error) {
      console.error("Error fetching AI suggested roles:", error);
      handleApiError(error, {
        showModal: true,
        message: t("jobRoles.aiSuggested.fetchFailed"),
        error: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveAiGeneratedOrganizationRolesApiHandler = async () => {
    try {
      setIsLoading(true);
      //   "mappedJobRoles": [
      //     {
      //         "job_role": "xxxxxx",
      //         "mapped_departments": ["xxxx", "xxxxx", "xxxxxx"]
      //     }
      // ]
      const response = await saveAiGeneratedOrganizationRoles({
        mappedJobRoles: selectedJobRoles.map((roleName) => ({
          job_role: roleName,
          mapped_departments: aiRoleDepartmentMap[roleName] || [],
        })),
      });

      // const response = await saveAiGeneratedOrganizationRoles({
      //   roles: selectedJobRoles,
      // });
      // console.log(
      //   "AI Generated Organization Roles saved successfully:",
      //   response
      // );

      if (response.code === 200) {
        setShowCreateModal(false);
        setCreateStep(1);
        setCreateMode("");
        setSelectedJobRoles([]);
        setAiRoleDepartmentMap({});
        setAiRoleDepartmentSearchMap({});
        handleFilterData();
        showRoleSaveSuccess(t("jobRoles.roleCreatedSuccess"), newRole);
      }
    } catch (error) {
      console.error("Error saving AI generated organization roles:", error);
      handleApiError(error, {
        showModal: true,
        message: t("jobRoles.aiSuggested.saveFailed"),
        error: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // AI Suggested Roles (selection helpers used inside Create modal)
  const areAllSuggestedRolesSelected =
    aiSuggestedRoles.length > 0 &&
    selectedJobRoles.length === aiSuggestedRoles.length;

  const toggleSelectAllSuggestedRoles = () => {
    if (areAllSuggestedRolesSelected) {
      setSelectedJobRoles([]);
      setAiRoleDepartmentMap({});
      setAiRoleDepartmentSearchMap({});
    } else {
      setSelectedJobRoles(aiSuggestedRoles);
      setAiRoleDepartmentMap((prev) =>
        aiSuggestedRoles.reduce((acc, roleName) => {
          acc[roleName] = prev[roleName] || [];
          return acc;
        }, {}),
      );
      setAiRoleDepartmentSearchMap((prev) =>
        aiSuggestedRoles.reduce((acc, roleName) => {
          acc[roleName] = prev[roleName] || "";
          return acc;
        }, {}),
      );
    }
  };

  // Load employees when create modal opens with employee mode
  useEffect(() => {
    if (showCreateModal && createMode === "employee") {
      loadEmployees();
    }
  }, [showCreateModal, createMode]);

  // Fetch departments on component mount
  useEffect(() => {
    getDepartmentsDropDownApiHandler();
  }, []);

  useEffect(() => {
    loadPartnerRoleSharingSettings();
  }, []);

  // API call to get all job roles
  const handleFilterData = async () => {
    try {
      setIsLoading(true);
      const requestData = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        searchFilter: searchTerm,
        department: filterDepartment,
        level: filterLevel,
        roleAvailability: filterHiringStatus,
        hiringStatus: filterHiringStatus,
      };

      const response = await getAllJobRolesApi(requestData);

      const transformedRoles = response.data.map((role) => ({
        id: role._id,
        jobId: role.jobId,
        name: role.roleName,
        department: role.department,
        level: role.level,
        description: role.shortDescription || role.fullDescription || "",
        shortDescription: role.shortDescription || "",
        fullDescription: role.fullDescription || "",
        visibility: role.visibility,

        createdAt: role.createdAt,
        availability: role.availability,

        openForHiring:
          role.availability ??
          role.openForHiring ??
          role.isOpenForHiring ??
          true,

        roleAvailability: normalizeRoleAvailability(
          role.roleAvailability || role.hiringStatus,
          role.availability ??
            role.openForHiring ??
            role.isOpenForHiring ??
            true,
        ),

        hiringStatus: normalizeRoleAvailability(
          role.roleAvailability || role.hiringStatus,
          role.availability ??
            role.openForHiring ??
            role.isOpenForHiring ??
            true,
        ),

        // Keep the remaining fields unchanged
      }));
      setJobRoles(transformedRoles);
      setJobRolesLength(response.totalCount || transformedRoles.length);
    } catch (error) {
      console.error("Error fetching job roles:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterDepartment && filterDepartment !== "All") count++;
    if (filterLevel && filterLevel !== "All") count++;
    if (filterHiringStatus && filterHiringStatus !== "All") count++;
    if (canUsePartnerRoleSharing && filterRoleAccess !== ROLE_ACCESS.ALL)
      count++;
    if (viewMode === "card" && sortBy !== "name") count++;
    return count;
  };

  // GET DEPARTMENTS DROPDOWN DATA (Copied from UpskillPage)
  const getDepartmentsDropDownApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      // Add "All" option in the departments dropdown to show all departments on initial load
      setDepartments([{ name: t("common.all"), _id: "All" }, ...response.data]);
      setAiDepartmentList([...response.data]);
    } catch (error) {
      console.error("Error fetching departments:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const normalizeSharedDataValue = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const isJobRoleSharingEnabledForPartner = (partner = {}) => {
    // Support the previous API format.
    if (partner.jobRolesShare === true) return true;

    // Support the latest API format:
    // sharedData: ["Departments", "Job Roles"]
    if (!Array.isArray(partner.sharedData)) return false;

    return partner.sharedData.some((item) => {
      const normalizedItem = normalizeSharedDataValue(item);

      return normalizedItem === "jobrole" || normalizedItem === "jobroles";
    });
  };

  const loadPartnerRoleSharingSettings = async () => {
    try {
      const response = await getPartnersListApi();

      const partnerRows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response)
            ? response
            : [];

      const mappedPartners = partnerRows
        .map((partner) => ({
          id:
            partner._id ||
            partner.id ||
            partner.partnerTenantId ||
            partner.tenantId ||
            "",

          name:
            partner.partnerOrganization ||
            partner.organization ||
            partner.partnerName ||
            partner.name ||
            "Partner",

          admin: partner.partnerSuperAdminName || partner.adminName || "",

          email: partner.partnerSuperAdminEmail || partner.adminEmail || "",

          orgCode: partner.partnerOrgCode || partner.orgCode || "",

          status: String(partner.status || "")
            .trim()
            .toUpperCase(),

          jobRolesShare: isJobRoleSharingEnabledForPartner(partner),

          sharedData: Array.isArray(partner.sharedData)
            ? partner.sharedData
            : [],

          sharedJobRoles: Array.isArray(partner.jobRoles)
            ? partner.jobRoles
            : [],
        }))
        .filter((partner) => Boolean(partner.id));

      const eligiblePartners = mappedPartners.filter((partner) => {
        const isConnectedStatus = ["ACTIVE", "ACCEPTED", "CONNECTED"].includes(
          partner.status,
        );

        return isConnectedStatus && partner.jobRolesShare;
      });

      setPartnerSharingSettings({
        hasActivePartnerConnections: eligiblePartners.length > 0,
        jobRoleSharingEnabled: eligiblePartners.length > 0,
        activePartners: eligiblePartners,
        allPartners: mappedPartners,
      });
    } catch (error) {
      console.error("Error loading partner role sharing settings:", error);

      setPartnerSharingSettings({
        hasActivePartnerConnections: false,
        jobRoleSharingEnabled: false,
        activePartners: [],
        allPartners: [],
      });
    }
  };
  // Load employees from API
  const loadEmployees = async () => {
    try {
      setIsLoadingEmployees(true);
      const response = await getAllPeopleApi();

      if (response.code === 200 && response.data) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Fetch skill values based on selected employees
  const fetchSkillValuesFromEmployees = async (employeeUserIds) => {
    try {
      setIsLoadingSkillValues(true);
      const response = await getPeopleSkillValuesApi({
        userIdArray: employeeUserIds,
      });

      if (response.code === 200 && response.data) {
        const skillData = response.data;

        // Update newRole with fetched skill values
        setNewRole((prev) => ({
          ...prev,
          skillValues: {
            adaptability: (skillData.Adaptability || 5).toString(),
            communication: (skillData.Communication || 5).toString(),
            creativity: (skillData.Creativity || 5).toString(),
            attentionToDetail: (
              skillData["Attention to Detail"] || 5
            ).toString(),
            problemSolving: (skillData["Problem-Solving"] || 5).toString(),
            teamWork: (skillData["Team work"] || 5).toString(),
            leadership: (skillData.Leadership || 5).toString(),
            timeManagement: (skillData["Time Management"] || 5).toString(),
            interpersonalSkills: (
              skillData["Interpersonal Skills"] || 5
            ).toString(),
            workEthic: (skillData["Work Ethic"] || 5).toString(),
            professionValue: (skillData.ProfessionScore || 5).toString(),
            purposeValue: (skillData.PurposeScore || 5).toString(),
            spiritValue: (skillData.SpiritScore || 5).toString(),
            rewardValue: (skillData.RewardsScore || 5).toString(),
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching skill values:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoadingSkillValues(false);
    }
  };

  // Filter job roles based on search and filters
  const filteredRoles = jobRoles.filter((role) => {
    if (role.isOwnedByCurrentOrganization === false) return false;

    if (
      canUsePartnerRoleSharing &&
      filterRoleAccess !== ROLE_ACCESS.ALL &&
      getRoleAccess(role) !== filterRoleAccess
    ) {
      return false;
    }

    return true;
  });

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  // Sort the filtered roles
  const sortedRoles = [...filteredRoles].sort((a, b) => {
    const getRoleSortValue = (role) => {
      switch (sortBy) {
        case "hiringStatus":
          return getRoleAvailability(role);
        case "roleAccess":
          return getRoleAccess(role);
        case "createdAt":
          return role.createdAt ? new Date(role.createdAt).getTime() : null;
        default:
          return role[sortBy] ?? "";
      }
    };

    const aValue = getRoleSortValue(a);
    const bValue = getRoleSortValue(b);

    const aIsEmpty = aValue === null || aValue === undefined || aValue === "";
    const bIsEmpty = bValue === null || bValue === undefined || bValue === "";

    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1;
    if (bIsEmpty) return -1;

    const comparison =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: "base",
          });

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const isTrueEmptyState =
    !isLoading && sortedRoles.length === 0 && getAppliedFiltersCount() === 0;

  // Handle bulk selection
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRoles(sortedRoles.map((role) => role.id)); // Store _id
    } else {
      setSelectedRoles([]);
    }
  };

  const handleSelectRole = (roleId, checked) => {
    // roleId here is the _id
    if (checked) {
      setSelectedRoles((prev) => [...prev, roleId]);
    } else {
      setSelectedRoles((prev) => prev.filter((id) => id !== roleId));
    }
  };

  const isAllSelected =
    sortedRoles.length > 0 && selectedRoles.length === sortedRoles.length;
  const isIndeterminate =
    selectedRoles.length > 0 && selectedRoles.length < sortedRoles.length;

  // Handle bulk delete
  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setIsLoading(true);
      const jobIdsToDelete = selectedRoles
        .map((selectedId) => {
          const role = jobRoles.find((r) => r.id === selectedId); // Find role by _id
          return role ? role.jobId : null; // Get the jobId
        })
        .filter(Boolean); // Filter out any nulls if a role wasn't found

      if (jobIdsToDelete.length === 0) {
        setShowSuccessNotification({
          show: true,
          message: t("jobRoles.noValidRolesSelected"),
        });
        setIsLoading(false);
        return;
      }

      for (const jobId of jobIdsToDelete) {
        await deleteJobRole({ jobId: jobId }); // Call delete API with jobId
      }
      setSelectedRoles([]);
      setShowBulkDeleteModal(false);
      setShowSuccessNotification({
        show: true,
        message: t("jobRoles.bulkDeleteSuccess", {
          count: jobIdsToDelete.length,
        }),
      });
      handleFilterData(); // Refresh data after bulk deletion
    } catch (error) {
      console.error("Error performing bulk delete:", error);
      handleApiError(error, { showModal: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle share functionality
  const handleShare = (role) => {
    setShowShareModal({ show: true, role });
  };

  const getShareUrl = (role) => {
    return `${window.location.origin}/job-roles/${role.id}`;
  };

  const handleSharePlatform = (platform, role) => {
    const url = getShareUrl(role);
    const text = t("jobRoles.shareText", {
      roleName: role.name,
      department: role.department,
    });

    switch (platform) {
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
          "_blank",
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url,
          )}`,
          "_blank",
        );
        break;
      case "email":
        window.open(
          `mailto:?subject=${encodeURIComponent(
            t("jobRoles.emailSubject", { roleName: role.name }),
          )}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
          "_blank",
        );
        break;
      case "copy":
        navigator.clipboard.writeText(url).then(() => {
          setShowSuccessNotification({
            show: true,
            message: t("jobRoles.linkCopied"),
          });
        });
        break;
      default:
        break;
    }
    setShowShareModal({ show: false, role: null });
  };

  // Get sort icon for table headers
  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ChevronsUpDown size={14} className="sort-icon" />;
    }

    return sortOrder === "asc" ? (
      <ChevronUp size={14} className="sort-icon active" />
    ) : (
      <ChevronDown size={14} className="sort-icon active" />
    );
  };

  // Handle role creation
  const handleCreateRole = useCallback(() => {
    setShowCreateModal(true);
    setCreateStep(1); // Start at step 1
    setCreateMode("");
    setNewRole({
      roleName: "",
      department: "",
      level: "Executive or Senior Management",
      shortDescription: "",
      fullDescription: "",
      visibility: "Public",
      openForHiring: true,
      hiringStatus: ROLE_AVAILABILITY.AVAILABLE,
      roleAvailability: ROLE_AVAILABILITY.AVAILABLE,
      roleAccess: ROLE_ACCESS.PRIVATE,
      selectedPartnerIds: [],
      createdFrom: "Scratch",
      peopleUsedForCreation: [],
      skillValues: {
        adaptability: "5.0",
        communication: "5.0",
        creativity: "5.0",
        attentionToDetail: "5.0",
        problemSolving: "5.0",
        teamWork: "5.0",
        leadership: "5.0",
        timeManagement: "5.0",
        interpersonalSkills: "5.0",
        workEthic: "5.0",
        professionValue: "5.0",
        purposeValue: "5.0",
        spiritValue: "5.0",
        rewardValue: "5.0",
      },
    });
    setSelectedEmployee(null);
    setSelectedEmployees([]);
    setEmployeeSearchTerm("");
    setNewRolePartnerSearch("");
    setSelectedJobRoles([]);
    setAiRoleDepartmentMap({});
    setAiRoleDepartmentSearchMap({});
    // Clear all form errors when opening the modal
    setRoleNameError("");
    setDepartmentError("");
    setLevelError("");
    setShortDescriptionError("");
  }, []);

  useEffect(() => {
    if (location.state?.openCreateJobRoleModal) {
      handleCreateRole();
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [
    location.state,
    location.pathname,
    location.search,
    navigate,
    handleCreateRole,
  ]);

  // Handle attribute slider change
  const handleAttributeChange = (attribute, value) => {
    setNewRole((prev) => ({
      ...prev,
      skillValues: {
        ...prev.skillValues,
        [attribute]: value.toString(),
      },
    }));
  };

  // Handle employee selection for employee-based creation
  const handleSelectEmployee = (employee) => {
    const isSelected = selectedEmployees.some(
      (emp) => emp._id === employee._id,
    );

    if (isSelected) {
      // Remove employee
      const updatedEmployees = selectedEmployees.filter(
        (emp) => emp._id !== employee._id,
      );
      setSelectedEmployees(updatedEmployees);

      // Update newRole
      setNewRole((prev) => ({
        ...prev,
        peopleUsedForCreation: updatedEmployees.map((emp) => emp.userId),
      }));
    } else {
      // Add employee
      const updatedEmployees = [...selectedEmployees, employee];
      setSelectedEmployees(updatedEmployees);

      // Update newRole
      setNewRole((prev) => ({
        ...prev,
        createdFrom: "People",
        peopleUsedForCreation: updatedEmployees.map((emp) => emp.userId),
      }));

      // Fetch skill values for all selected employees
      fetchSkillValuesFromEmployees(updatedEmployees.map((emp) => emp.userId));
    }
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    const nextValue =
      field === "shortDescription" ? limitShortDescription(value) : value;
    setNewRole((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
    // Clear specific error when input changes
    if (field === "roleName") setRoleNameError("");
    if (field === "department") setDepartmentError("");
    if (field === "level") setLevelError("");
    if (field === "shortDescription") setShortDescriptionError("");
  };

  // Handle edit form input change
  const handleEditInputChange = (field, value) => {
    const nextValue =
      field === "shortDescription" ? limitShortDescription(value) : value;
    setEditRole((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  useEffect(() => {
    if (!descriptionRole) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setDescriptionRole(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [descriptionRole]);

  const handleSubmitRole = async () => {
    let hasError = false;

    // Validate mandatory fields
    if (!newRole.roleName.trim()) {
      setRoleNameError(t("jobRoles.errors.roleNameRequired"));
      hasError = true;
    } else {
      setRoleNameError("");
    }

    if (!newRole.department.trim()) {
      setDepartmentError(t("jobRoles.errors.departmentRequired"));
      hasError = true;
    } else {
      setDepartmentError("");
    }

    if (!newRole.level.trim()) {
      setLevelError(t("jobRoles.errors.levelRequired"));
      hasError = true;
    } else {
      setLevelError("");
    }

    if (!newRole.shortDescription.trim()) {
      setShortDescriptionError(t("jobRoles.errors.shortDescriptionRequired"));
      hasError = true;
    } else {
      setShortDescriptionError("");
    }

    if (
      getRoleAvailability(newRole) === ROLE_AVAILABILITY.CLOSED &&
      newRole.roleAccess === ROLE_ACCESS.PUBLIC
    ) {
      setShortDescriptionError("Closed roles cannot be shared with partners.");
      return;
    }

    if (
      canUsePartnerRoleSharing &&
      getRoleAvailability(newRole) !== ROLE_AVAILABILITY.CLOSED &&
      newRole.roleAccess === ROLE_ACCESS.PUBLIC &&
      (newRole.selectedPartnerIds || []).length === 0
    ) {
      MessageManager.showToast(
        "warning",
        "Please select at least one partner before sharing this role.",
      );
      return;
    }
    if (hasError) {
      return; // Stop submission if there are errors
    }

    try {
      if (
        newRole.createdFrom === "People" &&
        (!newRole.peopleUsedForCreation ||
          newRole.peopleUsedForCreation.length === 0)
      ) {
        setShowSuccessNotification({
          show: true,
          message: t("jobRoles.selectEmployeeRequired"),
        });
        return;
      }

      setIsLoading(true);

      const roleData = {
        roleName: newRole.roleName,
        shortDescription: newRole.shortDescription,
        fullDescription: newRole.fullDescription.trim(),
        department: newRole.department,
        level: newRole.level,
        createdFrom: newRole.createdFrom,

        // Available = true, Closed = false
        availability: isRoleAvailable(newRole),

        visibility: canUsePartnerRoleSharing
          ? newRole.roleAccess
          : ROLE_ACCESS.PRIVATE,

        partnerIds:
          canUsePartnerRoleSharing &&
          newRole.roleAccess === ROLE_ACCESS.PUBLIC &&
          isRoleAvailable(newRole)
            ? newRole.selectedPartnerIds
            : [],

        peopleUsedForCreation: newRole.peopleUsedForCreation,

        skillValues: {
          Adaptability: newRole.skillValues.adaptability,
          Communication: newRole.skillValues.communication,
          Creativity: newRole.skillValues.creativity,
          "Attention to Detail": newRole.skillValues.attentionToDetail,
          "Problem-Solving": newRole.skillValues.problemSolving,
          "Team work": newRole.skillValues.teamWork,
          Leadership: newRole.skillValues.leadership,
          "Time Management": newRole.skillValues.timeManagement,
          "Interpersonal Skills": newRole.skillValues.interpersonalSkills,
          "Work Ethic": newRole.skillValues.workEthic,
          professionScore: newRole.skillValues.professionValue,
          purposeScore: newRole.skillValues.purposeValue,
          spiritScore: newRole.skillValues.spiritValue,
          rewardsScore: newRole.skillValues.rewardValue,
        },
      };
      const response = await createJobRoleApi(roleData);

      if (response.code === 201) {
        setShowCreateModal(false);
        setCreateStep(1);
        setCreateMode("");
        handleFilterData();
        MessageManager.showToast("success", t("jobRoles.roleCreatedSuccess"));
      }
    } catch (error) {
      console.error("Error creating job role:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openBulkSharePartnerModal = (roleIds = selectedRoles) => {
    const { publicRoles, privateRoles } = getBulkShareRoleBuckets(roleIds);

    if (publicRoles.length === 0) {
      MessageManager.showToast(
        "error",
        "Only public roles can be shared with partners.",
      );
      return;
    }

    if (privateRoles.length > 0) {
      MessageManager.showToast(
        "warning",
        getBulkSharePrivateRoleMessage(privateRoles.length),
      );
    }

    setBulkSharePartnerSelection("");
    setBulkSharePartnerSearch("");
    setShowBulkSharePartnerResults(false);
    setBulkSharePartnerModalOpen(true);
  };

  // BULK SHARE JOB ROLES WITH PARTNERS
  const handleBulkShareWithPartner = async () => {
    try {
      const { publicRoles } = getBulkShareRoleBuckets();

      const publicJobIds = publicRoles
        .map((role) => role.jobId)
        .filter(Boolean);

      if (publicRoles.length === 0) {
        MessageManager.showToast(
          "error",
          "Only public roles can be shared with partners.",
        );
        return;
      }

      if (publicJobIds.length === 0) {
        MessageManager.showToast(
          "error",
          "No valid public job roles selected to share.",
        );
        return;
      }

      const obj = {
        partnerIds: [bulkSharePartnerSelection],
        jobIds: publicJobIds,
      };
      setIsLoading(true);
      await shareJobRolesWithPartnersApi(obj);

      MessageManager.showToast(
        "success",
        "Changes saved",
      );

      setBulkSharePartnerModalOpen(false);
      setBulkSharePartnerSearch("");
      setBulkSharePartnerSelection("");
      setShowBulkSharePartnerResults(false);
      setSelectedRoles([]);
    } catch (error) {
      console.error("Error sharing job roles with partners:", error);
      handleApiError(error, { showModal: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle role deletion
  const handleDeleteRole = (roleId) => {
    // roleId here is the _id
    setShowDeleteModal({ show: true, roleId });
  };

  const confirmDeleteRole = async () => {
    if (showDeleteModal.roleId) {
      try {
        setIsLoading(true);
        // Find the job role object to get its jobId
        const roleToDelete = jobRoles.find(
          (role) => role.id === showDeleteModal.roleId,
        );
        if (roleToDelete && roleToDelete.jobId) {
          const response = await deleteJobRole({ jobId: roleToDelete.jobId }); // Call delete API with jobId
          if (response.code === 200) {
            setShowDeleteModal({ show: false, roleId: null });
            setSuccessModal({
              show: true,
              title: t("jobRoles.delete.successTitle"),
              message: t("jobRoles.delete.successMessage", {
                name: roleToDelete.name,
              }),
            });
            handleFilterData(); // Refresh data after deletion
          }
        } else {
          handleApiError(
            new Error(t("jobRoles.errors.jobIdNotFoundForDeletion")),
            {
              showModal: true,
            },
          );
        }
      } catch (error) {
        console.error("Error deleting job role:", error);
        handleApiError(error, { showModal: true });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle role editing
  const handleEditRole = async (role) => {
    try {
      setIsLoading(true);

      const response = await getJobRoleById({
        jobId: role.jobId,
      });

      /*
       * Supports:
       * 1. { code: 200, data: role }
       * 2. Axios response: { data: { code: 200, data: role } }
       * 3. Direct role response
       */
      const fetchedRole = response?.data?.data ?? response?.data ?? response;

      if (!fetchedRole?.jobId) {
        throw new Error(t("jobRoles.errors.fetchJobRoleDetailsFailed"));
      }

      const availability =
        typeof fetchedRole.availability === "boolean"
          ? fetchedRole.availability
          : (fetchedRole.openForHiring ?? fetchedRole.isOpenForHiring ?? true);

      const roleAvailability = availability
        ? ROLE_AVAILABILITY.AVAILABLE
        : ROLE_AVAILABILITY.CLOSED;

      const partnerIds = Array.isArray(fetchedRole.partnerIds)
        ? fetchedRole.partnerIds
        : Array.isArray(fetchedRole.sharedPartnerIds)
          ? fetchedRole.sharedPartnerIds
          : Array.isArray(fetchedRole.sharedWithPartners)
            ? fetchedRole.sharedWithPartners
            : [];

      const roleAccess =
        fetchedRole.visibility === ROLE_ACCESS.PUBLIC || partnerIds.length > 0
          ? ROLE_ACCESS.PUBLIC
          : ROLE_ACCESS.PRIVATE;

      setEditingRole({
        ...fetchedRole,
        availability,
        partnerIds,
      });

      setEditRole({
        jobId: fetchedRole.jobId || "",
        roleName: fetchedRole.roleName || "",
        department: fetchedRole.department || "",
        level: fetchedRole.level || "",

        shortDescription: limitShortDescription(
          fetchedRole.shortDescription || "",
        ),

        fullDescription: fetchedRole.fullDescription || "",

        // Boolean value returned by the API
        availability,

        // Compatibility fields used by the existing UI helpers
        openForHiring: availability,
        hiringStatus: roleAvailability,
        roleAvailability,

        visibility: roleAccess,
        roleAccess,
        selectedPartnerIds: partnerIds,

        education: Array.isArray(fetchedRole.education)
          ? fetchedRole.education
          : [],

        hardSkills: Array.isArray(fetchedRole.hardSkills)
          ? fetchedRole.hardSkills
          : [],

        majors: Array.isArray(fetchedRole.majors) ? fetchedRole.majors : [],

        certifications: Array.isArray(fetchedRole.certifications)
          ? fetchedRole.certifications
          : [],

        skillValues:
          fetchedRole.skillValues && typeof fetchedRole.skillValues === "object"
            ? fetchedRole.skillValues
            : {},
      });

      setSkillInput("");
      setEditRolePartnerSearch("");
      setShowEditModal(true);
    } catch (error) {
      console.error("Error fetching job role for edit:", error);

      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  // Generate job description using AI for edit form
  const generateEditJobDescription = async () => {
    if (!editRole.jobId) {
      setShowSuccessNotification({
        show: true,
        message: t("jobRoles.jobIdRequired"),
      });
      return;
    }

    try {
      setIsGeneratingDescription(true);

      const aiResponse = await aiJobDescriptionGenerationApi({
        jobId: editRole.jobId,
      });

      if (aiResponse.code === 200 && aiResponse.data) {
        // Parse the AI response to extract different sections
        const responseData = aiResponse.data;

        // Extract education from qualifications section
        const qualificationsMatch = responseData.match(
          /"Qualifications":\s*"([^"]*)"/,
        );
        const skillsMatch = responseData.match(/"Skills":\s*"([^"]*)"/);
        const requirementsMatch = responseData.match(
          /"Job Requirements":\s*"([^"]*)"/,
        );

        if (qualificationsMatch) {
          // Extract education requirements
          const qualifications = qualificationsMatch[1];
          const educationKeywords = [
            "Bachelor",
            "Master",
            "PhD",
            "degree",
            "certification",
            "diploma",
          ];
          const extractedEducation = [];

          educationKeywords.forEach((keyword) => {
            if (qualifications.toLowerCase().includes(keyword.toLowerCase())) {
              if (keyword === "Bachelor" || keyword === "degree") {
                extractedEducation.push("Bachelor's Degree");
              } else if (keyword === "Master") {
                extractedEducation.push("Master's Degree");
              } else if (keyword === "PhD") {
                extractedEducation.push("PhD");
              } else if (keyword === "certification") {
                extractedEducation.push("Professional Certification");
              }
            }
          });

          setEditRole((prev) => ({
            ...prev,
            education: [...new Set([...prev.education, ...extractedEducation])],
          }));
        }

        if (skillsMatch) {
          // Extract skills
          const skills = skillsMatch[1];
          const skillsList =
            skills.match(/\b[A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)*\b/g) || [];
          const filteredSkills = skillsList
            .filter(
              (skill) =>
                skill.length > 2 &&
                ![
                  "The",
                  "This",
                  "Should",
                  "Must",
                  "Will",
                  "Can",
                  "Are",
                  "Is",
                ].includes(skill),
            )
            .slice(0, 10); // Limit to 10 skills

          setEditRole((prev) => ({
            ...prev,
            hardSkills: [...new Set([...prev.hardSkills, ...filteredSkills])],
          }));
        }

        // Combine all sections for full description
        const fullDescription = [
          qualificationsMatch ? qualificationsMatch[1] : "",
          skillsMatch ? skillsMatch[1] : "",
          requirementsMatch ? requirementsMatch[1] : "",
        ]
          .filter(Boolean)
          .join("\n\n");

        setEditRole((prev) => ({
          ...prev,
          fullDescription: fullDescription,
        }));

        MessageManager.showToast(
          "success",
          t("jobRoles.descriptionGeneratedSuccess"),
        );
      }
    } catch (error) {
      console.error("Error generating job description:", error);
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Handle skill input
  const handleSkillInputKeyPress = (e) => {
    if (e.key === "Enter" && skillInput.trim()) {
      e.preventDefault();
      const newSkills = skillInput
        .split(",")
        .map((skill) => skill.trim())
        .filter((skill) => skill);
      setEditRole((prev) => ({
        ...prev,
        hardSkills: [...new Set([...prev.hardSkills, ...newSkills])],
      }));
      setSkillInput("");
    }
  };

  // Remove skill
  const removeSkill = (skillToRemove) => {
    setEditRole((prev) => ({
      ...prev,
      hardSkills: prev.hardSkills.filter((skill) => skill !== skillToRemove),
    }));
  };

  // Remove education
  const removeEducation = (educationToRemove) => {
    setEditRole((prev) => ({
      ...prev,
      education: prev.education.filter((edu) => edu !== educationToRemove),
    }));
  };

  const submitEditRoleToApi = async (forceDeactivatePartnerSharing = false) => {
    try {
      setIsLoading(true);

      const availability = isRoleAvailable(editRole);

      const resolvedAccess = !availability
        ? ROLE_ACCESS.PRIVATE
        : canUsePartnerRoleSharing
          ? editRole.roleAccess
          : ROLE_ACCESS.PRIVATE;

      const editData = {
        jobId: editRole.jobId,
        roleName: editRole.roleName.trim(),
        department: editRole.department,
        level: editRole.level,

        shortDescription: editRole.shortDescription.trim(),

        fullDescription: editRole.fullDescription.trim(),

        education: editRole.education || [],
        hardSkills: editRole.hardSkills || [],
        majors: editRole.majors || [],
        certifications: editRole.certifications || [],
        skillValues: editRole.skillValues || {},

        // Available = true, Closed = false
        availability,

        // Keep API naming consistent with getJobRoleById
        visibility: resolvedAccess,

        partnerIds:
          resolvedAccess === ROLE_ACCESS.PUBLIC
            ? editRole.selectedPartnerIds || []
            : [],

        deactivatePartnerRoleShares: forceDeactivatePartnerSharing,
      };

      const response = await editJobRoleApi(editData);

      if (
        response?.code === 200 ||
        response?.code === 201 ||
        response?.data?.code === 200 ||
        response?.data?.code === 201
      ) {
        setShowEditModal(false);
        setEditingRole(null);

        setShowCloseSharedConfirm({
          show: false,
          mode: "",
        });

        await handleFilterData();

        showRoleSaveSuccess(t("jobRoles.roleUpdatedSuccess"), {
          ...editRole,
          availability,
          roleAvailability: availability
            ? ROLE_AVAILABILITY.AVAILABLE
            : ROLE_AVAILABILITY.CLOSED,
          visibility: resolvedAccess,
          roleAccess: resolvedAccess,
        });
      }
    } catch (error) {
      console.error("Error updating job role:", error);

      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit form submission
  const handleSubmitEditRole = async () => {
    const resolvedAvailability = getRoleAvailability(editRole);

    if (
      resolvedAvailability === ROLE_AVAILABILITY.CLOSED &&
      editRole.roleAccess === ROLE_ACCESS.PUBLIC
    ) {
      if (editingRole && hasRolePartnerSharing(editingRole)) {
        setShowCloseSharedConfirm({ show: true, mode: "edit" });
        return;
      }

      setEditRole((prev) => ({
        ...prev,
        roleAccess: ROLE_ACCESS.PRIVATE,
        selectedPartnerIds: [],
      }));
    }

    if (
      canUsePartnerRoleSharing &&
      resolvedAvailability !== ROLE_AVAILABILITY.CLOSED &&
      editRole.roleAccess === ROLE_ACCESS.PUBLIC &&
      (editRole.selectedPartnerIds || []).length === 0
    ) {
      MessageManager.showToast(
        "warning",
        "Please select at least one partner before sharing this role.",
      );
      return;
    }

    await submitEditRoleToApi(false);
  };

  const filteredEmployees = employees.filter((employee) =>
    employee.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()),
  );

  const sanitizeSearch = (val) =>
    // allow letters, numbers, space, dash, underscore, and dot; strip everything else (incl. backslash)
    val.replace(/[\\`~!@#$%^&*()+={}\[\]|:;"'<>,.?/]/g, "");

  const handleSearchButtonClick = () => {
    setCurrentPage(1);
    setSearchTerm(searchInputValue.trim());
    setShowSearchBar(false);
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };
  // const rolePartnerOptions = Array.isArray(
  //   partnerSharingSettings.activePartners,
  // )
  //   ? partnerSharingSettings.activePartners
  //   : [];

  const eligibleRolePartnerOptions = Array.isArray(
    partnerSharingSettings.activePartners,
  )
    ? partnerSharingSettings.activePartners
    : [];

  const filteredBulkSharePartnerOptions = useMemo(() => {
    const searchValue = bulkSharePartnerSearch.trim().toLowerCase();

    if (!searchValue) {
      return eligibleRolePartnerOptions;
    }

    return eligibleRolePartnerOptions.filter((partner) =>
      [
        partner.name,
        partner.orgCode,
        partner.admin,
        partner.adminName,
        partner.email,
        partner.adminEmail,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchValue),
    );
  }, [bulkSharePartnerSearch, eligibleRolePartnerOptions]);

  const selectedBulkRoleSharePartner =
    eligibleRolePartnerOptions.find(
      (partner) => partner.id === bulkSharePartnerSelection,
    ) || null;

  const bulkShareRoleBuckets = getBulkShareRoleBuckets();
  const bulkSharePublicRoleCount = bulkShareRoleBuckets.publicRoles.length;
  const bulkSharePrivateRoleCount = bulkShareRoleBuckets.privateRoles.length;

  useEffect(() => {
    const handleBulkPartnerOutsideClick = (event) => {
      if (
        bulkSharePartnerPickerRef.current &&
        !bulkSharePartnerPickerRef.current.contains(event.target)
      ) {
        setShowBulkSharePartnerResults(false);
      }
    };

    document.addEventListener("mousedown", handleBulkPartnerOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleBulkPartnerOutsideClick);
    };
  }, []);

  const renderRolePartnerSearchSelect = ({
    selectedIds = [],
    searchValue = "",
    onSearchChange,
    onToggle,
  }) => {
    const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];

    const safeSearchValue = String(searchValue || "");

    const selectedPartners = eligibleRolePartnerOptions.filter((partner) =>
      safeSelectedIds.includes(partner.id),
    );

    const filteredPartners = eligibleRolePartnerOptions.filter((partner) => {
      const searchText = [
        partner.name,
        partner.admin,
        partner.email,
        partner.orgCode,
        partner.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchText.includes(safeSearchValue.trim().toLowerCase());
    });

    return (
      <div className="jr-partner-search-select">
        <div className="jr-partner-search-head">
          <div>
            <strong>Select Partners</strong>
          </div>

          <span>{safeSelectedIds.length} selected</span>
        </div>

        <div className="jr-partner-search-box">
          <Search size={16} />

          <input
            type="text"
            value={safeSearchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search partners by name, admin, email, or organization code"
          />

          {safeSearchValue && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label="Clear partner search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {selectedPartners.length > 0 && (
          <div className="jr-selected-partner-pills">
            {selectedPartners.map((partner) => (
              <span key={partner.id}>
                {partner.name}

                <button
                  type="button"
                  onClick={() => onToggle(partner.id, false)}
                  aria-label={`Remove ${partner.name}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="jr-partner-option-list">
          {filteredPartners.length > 0 ? (
            filteredPartners.map((partner) => {
              const isSelected = safeSelectedIds.includes(partner.id);

              return (
                <button
                  type="button"
                  key={partner.id}
                  className={`jr-partner-option ${
                    isSelected ? "selected" : ""
                  }`}
                  onClick={() => onToggle(partner.id, !isSelected)}
                >
                  <span className="jr-partner-option-check">
                    {isSelected && <Check size={13} />}
                  </span>

                  <span className="jr-partner-option-main">
                    <strong>{partner.name}</strong>

                    <small>
                      {[
                        partner.admin,
                        partner.email,
                        partner.orgCode,
                        partner.status ? `Status: ${partner.status}` : "",
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </small>
                  </span>
                </button>
              );
            })
          ) : (
            <div className="jr-partner-empty">
              No partners were returned by the API.
            </div>
          )}
        </div>
      </div>
    );
  };
  const roleAccessOptionMeta = {
    [ROLE_ACCESS.PRIVATE]: {
      label: "Private",
      description:
        "Keep this role internal and visible only within your organization.",
      tooltip:
        "Private roles stay internal and are not shared with partner organizations.",
    },
    [ROLE_ACCESS.PUBLIC]: {
      label: "Public",
      description: "Share this job role with selected partner organizations.",
      tooltip:
        "Public shares the role with the partner organizations you select below.",
    },
  };

  const renderRoleAccessChooser = ({
    name,
    value,
    isDisabled = false,
    disabledAccesses = [],
    onChange,
  }) => (
    <div className="jr-role-access-options" role="radiogroup">
      {[ROLE_ACCESS.PRIVATE, ROLE_ACCESS.PUBLIC].map((access) => {
        const meta = roleAccessOptionMeta[access];
        const isSelected = value === access;
        const isOptionDisabled =
          isDisabled || disabledAccesses.includes(access);

        return (
          <label
            key={access}
            className={`jr-role-access-choice ${isSelected ? "selected" : ""} ${
              isOptionDisabled ? "disabled" : ""
            }`}
          >
            <input
              type="radio"
              name={name}
              value={access}
              checked={isSelected}
              disabled={isOptionDisabled}
              onChange={() => !isOptionDisabled && onChange(access)}
            />

            <span className="jr-role-access-choice-mark">
              {isSelected && <Check size={13} />}
            </span>

            <span className="jr-role-access-choice-content">
              <span className="jr-role-access-choice-title">
                {meta.label}
                <JobRoleModalTooltip
                  tooltip={{ title: meta.label, message: meta.tooltip }}
                  id={`${name}-${access}-tooltip`}
                >
                  <span className="jr-role-access-choice-tooltip">
                    <Info size={13} />
                  </span>
                </JobRoleModalTooltip>
              </span>

              <span className="jr-role-access-choice-desc">
                {meta.description}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
  return (
    <div className="job-roles-page">
      <main className="job-roles-main">
        <BackButton />

        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <Briefcase size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("jobRoles.pageTitle")}</h1>
              <JobRoleModalTooltip
                tooltip={{
                  title: t("jobRoles.tooltips.title"),
                  message: t("jobRoles.tooltips.message"),
                }}
                id="jobroles-tooltip"
              >
                <Info
                  size={18}
                  style={{
                    color: "var(--text-secondary)",
                  }}
                />
              </JobRoleModalTooltip>
            </div>
            <p>{t("jobRoles.pageDescription")}</p>
          </div>

          <div className="page-actions">
            <div
              className="view-toggle"
              style={{ position: "absolute", top: "105px", right: "260px" }}
            >
              <button
                className={`view-btn ${viewMode === "card" ? "active" : ""}`}
                onClick={() => setViewMode("card")}
                title={t("jobRoles.cardView")}
              >
                <Grid size={16} />
              </button>
              <button
                className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                title={t("jobRoles.listView")}
              >
                <List size={16} />
              </button>
            </div>
            {!isTrueEmptyState && (
              <button
                className="btn btn-primary create-team-btn"
                onClick={handleCreateRole}
                disabled={isLoading || !roleAccess.canAddRole}
                title={
                  showDisabledCreateRoleTooltip ? permissionDeniedMessage : ""
                }
              >
                <Plus size={16} />
                {t("jobRoles.createNewJobRole")}
              </button>
            )}
          </div>

          {/* Smart Search & Filter panel */}
          <div className="smart-filter-panel-teams">
            <div className="smart-filter-top-bar">
              {showSearchBar ? (
                <div className="smart-search-inline-row">
                  <input
                    type="text"
                    placeholder={t("jobRoles.searchPlaceholder")}
                    value={searchInputValue}
                    onChange={(e) =>
                      setSearchInputValue(sanitizeSearch(e.target.value))
                    }
                    className="smart-search-inline-input"
                    autoFocus
                  />
                  <span className="mobile-search-placeholder-hint">
                    {t("jobRoles.searchPlaceholder")}
                  </span>
                  <button
                    className="smart-filter-icon-btn active"
                    title={t("common.search")}
                    onClick={handleSearchButtonClick}
                  >
                    <Search size={18} />
                  </button>
                  {searchTerm && (
                    <button
                      className="smart-search-clear"
                      onClick={handleClearSearch}
                      title={t("common.clear")}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ) : null}

              <div className="smart-filter-icons-right">
                {!showSearchBar && (
                  <button
                    className={`smart-filter-icon-btn ${
                      showSearchBar ? "active" : ""
                    }`}
                    onClick={() => {
                      setSearchInputValue(searchTerm);
                      setShowSearchBar(true);
                      setShowFiltersPanel(false);
                    }}
                    title={t("common.search")}
                  >
                    <Search size={18} />
                  </button>
                )}
                <div className="smart-filter-separator" />
                <button
                  className={`smart-filter-icon-btn ${
                    showFiltersPanel ? "active" : ""
                  }`}
                  onClick={() => {
                    setShowFiltersPanel(!showFiltersPanel);
                    if (!showFiltersPanel) setShowSearchBar(false);
                  }}
                  title={t("common.filters")}
                >
                  <Filter size={18} />
                </button>
              </div>

              <div className="smart-filter-info">
                {getAppliedFiltersCount() > 0 && (
                  <>
                    <span className="smart-filter-badge">
                      {getAppliedFiltersCount()}
                    </span>
                    <span>{t("common.filtersApplied")}</span>
                  </>
                )}
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>{t("jobRoles.filters.department")}</label>
                      <select
                        value={filterDepartment}
                        onChange={(e) => setFilterDepartment(e.target.value)}
                        className={`smart-filter-select ${
                          filterDepartment !== departments[0]?.name
                            ? "smart-active"
                            : ""
                        }`}
                      >
                        {departments.map((dept) => (
                          <option key={dept._id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("jobRoles.filters.level")}</label>
                      <select
                        value={filterLevel}
                        onChange={(e) => setFilterLevel(e.target.value)}
                        className={`smart-filter-select ${
                          filterLevel !== "All" ? "smart-active" : ""
                        }`}
                      >
                        <option value="All">{t("common.all")}</option>
                        {levels.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>Role Availability</label>
                      <select
                        value={filterHiringStatus}
                        onChange={(e) => setFilterHiringStatus(e.target.value)}
                        className={`smart-filter-select ${
                          filterHiringStatus !== "All" ? "smart-active" : ""
                        }`}
                      >
                        {roleAvailabilityOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {canUsePartnerRoleSharing && (
                      <div className="smart-filter-item">
                        <label>
                          Role Type
                          <span
                            className="jr-inline-tooltip"
                            title="Controls whether this role is restricted to your organization or published to your active partner connections"
                          >
                            <Info size={13} />
                          </span>
                        </label>
                        <select
                          value={filterRoleAccess}
                          onChange={(e) => setFilterRoleAccess(e.target.value)}
                          className={`smart-filter-select ${
                            filterRoleAccess !== ROLE_ACCESS.ALL
                              ? "smart-active"
                              : ""
                          }`}
                        >
                          {roleAccessOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {viewMode === "card" && (
                      <div className="smart-filter-item">
                        <label>{t("jobRoles.filters.sortBy")}</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className={`smart-filter-select ${
                            sortBy !== "name" ? "smart-active" : ""
                          }`}
                        >
                          <option value="name">{t("common.name")}</option>
                          <option value="department">
                            {t("common.department")}
                          </option>
                          <option value="level">{t("jobRoles.level")}</option>
                          <option value="createdAt">
                            {t("jobRoles.createdDate")}
                          </option>
                          <option value="hiringStatus">
                            Role Availability
                          </option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* <div className="results-header">
            <h3>
              {isLoading
                ? t("common.loading")
                : `${jobRolesLength} ${t("jobRoles.results.jobRolesFound")
                    .replace("{count}", "")
                    .trim()}`}
            </h3>
          </div> */}

          <div className="u-page-actions">
            <label className="select-all-wrap">
              <input
                type="checkbox"
                checked={isAllSelected}
                disabled={sortedRoles.length === 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="role-checkbox"
              />
              <span>
                {t("common.selectAll")}
                {isAllSelected && sortedRoles.length > 0
                  ? ` (${sortedRoles.length})`
                  : ""}
              </span>
            </label>

            {/* <BulkAction
              options={[{ value: "delete", label: "Delete" }]}
              selectedIds={selectedRoles}
              onAction={(action) => {
                if (action === "delete") {
                  handleBulkDelete();
                }
              }}
              label={"Bulk Action"}
              requireSelection={true}
              placeholder={"Select bulk action"}
            /> */}
            <BulkAction
              options={[
                { value: "delete", label: t("common.delete") },
                { value: "share-with-partner", label: "Share with Partner" },
              ]}
              selectedIds={selectedRoles}
              onAction={(action, actionSelectedRoles) => {
                if (action === "delete") {
                  handleBulkDelete();
                }
                if (action === "share-with-partner") {
                  openBulkSharePartnerModal(actionSelectedRoles);
                }
              }}
              label={t("jobRoles.bulkActions.bulkAction")}
              requireSelection={true}
              placeholder={t("jobRoles.bulkActions.selectBulkAction")}
            />
          </div>

          {viewMode === "card" ? (
            <div className="job-roles-grid">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="job-role-card skeleton-card">
                    <div className="card-header skeleton-header" />
                    <div className="job-role-description">
                      <div className="skeleton-line" />
                      <div className="skeleton-line short" />
                    </div>
                    <div className="skeleton-button" />
                  </div>
                ))
              ) : sortedRoles.length === 0 ? (
                getAppliedFiltersCount() > 0 ? (
                  <FilterNoResults
                    title={t("common.noResults")}
                    message={t("common.adjustOrClear")}
                    clearLabel={t("common.clearFilters")}
                    onClear={clearAllFilters}
                  />
                ) : (
                  <EmptyState
                    icon={Briefcase}
                    iconSize={48}
                    title={t("jobRoles.emptyTitle")}
                    message={t("jobRoles.emptyMessage")}
                    actionLabel={t("jobRoles.createNewJobRole")}
                    onAction={handleCreateRole}
                  />
                )
              ) : (
                sortedRoles.map((role) => (
                  <div key={role.id} className="job-role-card">
                    <div className="job-role-header">
                      <div className="job-role-select">
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role.id)}
                          onChange={(e) =>
                            handleSelectRole(role.id, e.target.checked)
                          }
                          className="role-checkbox"
                        />
                      </div>
                      <div className="job-role-title">
                        <h3>{role.name}</h3>
                        <div className="job-role-meta">
                          <span className="job-department">
                            {role.department}
                          </span>
                          <span className="job-level">{role.level}</span>
                          <span
                            className={`hiring-status-badge ${getRoleStatusClass(getRoleAvailability(role))}`}
                          >
                            {/* {getRoleAvailability(role)} */}
                            {role.visibility}
                          </span>
                          {canUsePartnerRoleSharing && (
                            <span
                              className={`role-access-badge ${getRoleStatusClass(getRoleAccess(role))}`}
                            >
                              {getRoleAccess(role)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="job-role-description">
                      <p>{role.description}</p>
                    </div>

                    <div className="job-role-footer">
                      <div className="job-role-date">
                        {t("jobRoles.results.created")}:{" "}
                        {/* {new Date(role.createdAt).toLocaleDateString()} */}
                        {formatEmployeeCreatedAt(role.createdAt)}
                      </div>
                      <div className="job-role-actions">
                        <button
                          type="button"
                          className="job-action-btn view"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDescriptionRole(role);
                          }}
                          title="View long description"
                          aria-label={`View long description for ${role.name}`}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          disabled={!roleAccess.canEditRole}
                          className="job-action-btn edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditRole(role);
                          }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          disabled={!roleAccess.canDeleteRole}
                          className="job-action-btn delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRole(role.id);
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="job-roles-table">
              <div
                className={`job-table-header ${canUsePartnerRoleSharing ? "with-role-access" : ""}`}
              >
                <div className="job-table-col checkbox-col">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isIndeterminate;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="role-checkbox"
                  />
                </div>
                <div
                  className="job-table-col sortable"
                  onClick={() => handleSort("name")}
                >
                  {t("jobRoles.tableHeaders.jobRole")} {getSortIcon("name")}
                </div>
                <div
                  className="job-table-col sortable"
                  onClick={() => handleSort("department")}
                >
                  {t("jobRoles.tableHeaders.department")}{" "}
                  {getSortIcon("department")}
                </div>
                <div
                  className="job-table-col sortable"
                  onClick={() => handleSort("level")}
                >
                  {t("jobRoles.tableHeaders.level")} {getSortIcon("level")}
                </div>
                <div
                  className="job-table-col sortable"
                  onClick={() => handleSort("hiringStatus")}
                >
                  <span className="jr-table-label-with-info">
                    Role Availability
                    <span title="Available roles can appear in the Hiring module. Closed roles are hidden from active Hiring workflows by default">
                      <Info size={13} />
                    </span>
                  </span>
                  {getSortIcon("hiringStatus")}
                </div>
                {canUsePartnerRoleSharing && (
                  <div
                    className="job-table-col sortable"
                    onClick={() => handleSort("roleAccess")}
                  >
                    <span className="jr-table-label-with-info">
                      Role Type
                      <span title="Controls whether this role is restricted to your organization or published to your active partner connections">
                        <Info size={13} />
                      </span>
                    </span>
                    {getSortIcon("roleAccess")}
                  </div>
                )}
                <div
                  className="job-table-col sortable"
                  onClick={() => handleSort("createdAt")}
                >
                  {t("jobRoles.tableHeaders.created")}{" "}
                  {getSortIcon("createdAt")}
                </div>

                <div className="job-table-col">
                  {t("jobRoles.tableHeaders.actions")}
                </div>
              </div>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className={`job-table-row skeleton-row ${canUsePartnerRoleSharing ? "with-role-access" : ""}`}
                  >
                    <div className="job-table-col checkbox-col">
                      <div className="skeleton-checkbox" />
                    </div>
                    <div className="job-table-col job-name-col">
                      <div className="skeleton-line" />
                    </div>
                    <div className="job-table-col">
                      <div className="skeleton-line short" />
                    </div>
                    <div className="job-table-col">
                      <div className="skeleton-line short" />
                    </div>
                    <div className="job-table-col">
                      <div className="skeleton-line short" />
                    </div>
                    <div className="job-table-col">
                      <div className="skeleton-line short" />
                    </div>
                    <div className="job-table-col">
                      <div className="skeleton-buttons" />
                    </div>
                  </div>
                ))
              ) : sortedRoles.length === 0 ? (
                getAppliedFiltersCount() > 0 ? (
                  <FilterNoResults
                    title={t("common.noResults")}
                    message={t("common.adjustOrClear")}
                    clearLabel={t("common.clearFilters")}
                    onClear={clearAllFilters}
                  />
                ) : (
                  <EmptyState
                    icon={Briefcase}
                    iconSize={48}
                    title={t("jobRoles.emptyTitle")}
                    message={t("jobRoles.emptyMessage")}
                    actionLabel={t("jobRoles.createNewJobRole")}
                    onAction={handleCreateRole}
                  />
                )
              ) : (
                sortedRoles.map((role) => {
                  const isDescriptionExpanded =
                    expandedDescriptionRoleId === role.id;

                  return (
                    <Fragment key={role.id}>
                      <div
                        className={`job-table-row ${canUsePartnerRoleSharing ? "with-role-access" : ""} ${isDescriptionExpanded ? "is-expanded" : ""}`}
                      >
                        <div
                          className="job-table-col checkbox-col"
                          data-label=""
                        >
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes(role.id)}
                            onChange={(e) =>
                              handleSelectRole(role.id, e.target.checked)
                            }
                            className="role-checkbox"
                          />
                        </div>
                        <div
                          className="job-table-col job-name-col"
                          data-label={t("jobRoles.tableHeaders.jobRole")}
                        >
                          <div className="job-icon">
                            <Briefcase size={16} />
                          </div>
                          <div className="job-name-info">
                            <h4>{role.name}</h4>

                            <p className="job-short-description">
                              {role.shortDescription?.trim() ||
                                "No short description available."}
                            </p>

                            <button
                              type="button"
                              className="job-description-row-toggle"
                              onClick={() =>
                                setExpandedDescriptionRoleId((currentRoleId) =>
                                  currentRoleId === role.id ? null : role.id,
                                )
                              }
                              aria-expanded={isDescriptionExpanded}
                              aria-controls={`role-description-${role.id}`}
                            >
                              <span>
                                {isDescriptionExpanded
                                  ? "Hide description"
                                  : "View full description"}
                              </span>

                              {isDescriptionExpanded ? (
                                <ChevronUp size={15} aria-hidden="true" />
                              ) : (
                                <ChevronDown size={15} aria-hidden="true" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div
                          className="job-table-col"
                          data-label={t("jobRoles.tableHeaders.department")}
                        >
                          {role.department}
                        </div>
                        <div
                          className="job-table-col"
                          data-label={t("jobRoles.tableHeaders.level")}
                        >
                          {role.level}
                        </div>
                        <div
                          className="job-table-col"
                          data-label="Role Availability"
                        >
                          <span
                            className={`hiring-status-badge ${
                              role.availability ? "available" : "closed"
                            }`}
                          >
                            {role.availability ? "Available" : "Closed"}
                          </span>
                        </div>
                        {canUsePartnerRoleSharing && (
                          <div className="job-table-col" data-label="Role Type">
                            <span
                              className={`role-access-badge ${getRoleStatusClass(getRoleAccess(role))}`}
                            >
                              {getRoleAccess(role)}
                            </span>
                          </div>
                        )}
                        <div
                          className="job-table-col"
                          data-label={t("jobRoles.tableHeaders.created")}
                        >
                          {/* {new Date(role.createdAt).toLocaleDateString()} */}
                          {formatEmployeeCreatedAt(role.createdAt)}
                        </div>

                        <div
                          className="job-table-col"
                          data-label={t("jobRoles.tableHeaders.actions")}
                        >
                          <div className="job-table-actions">
                            <button
                              disabled={!roleAccess.canEditRole}
                              className="job-table-action-btn edit"
                              onClick={() => handleEditRole(role)}
                              title={
                                showDisabledEditRoleTooltip
                                  ? permissionDeniedMessage
                                  : ""
                              }
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              disabled={!roleAccess.canDeleteRole}
                              className="job-table-action-btn delete"
                              onClick={() => handleDeleteRole(role.id)}
                              title={
                                showDisabledDeleteRoleTooltip
                                  ? permissionDeniedMessage
                                  : ""
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {isDescriptionExpanded && (
                        <div
                          id={`role-description-${role.id}`}
                          className="job-table-description-row"
                          role="region"
                          aria-label={`Full description for ${role.name}`}
                        >
                          <div className="job-table-description-content">
                            <p>
                              {role.fullDescription?.trim() ||
                                "No long description has been added for this role."}
                            </p>
                          </div>
                        </div>
                      )}
                    </Fragment>
                  );
                })
              )}
            </div>
          )}

          {jobRolesLength > itemsPerPage && (
            <Pagination
              loading={isLoading}
              total={jobRolesLength}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={sortedRoles.length}
              onPageChange={setCurrentPage}
              pageSizeOptions={[20, 40, 60, 100]}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((jobRolesLength || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                `${t("jobRoles.pagination.showing")} ${start} ${t(
                  "jobRoles.pagination.to",
                )} ${end} ${t("jobRoles.pagination.of")} ${total} ${t(
                  "jobRoles.pagination.jobRoles",
                )}`
              }
            />
          )}
        </div>
      </main>

      {/* Create Job Role Modal */}
      {showCreateModal && (
        <div className="job-role-modal-overlay">
          <div className="job-role-modal">
            <div className="job-role-modal-header">
              <h2>{t("jobRoles.create.modalTitle")}</h2>
              <button
                className="job-role-modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="job-role-modal-content">
              {createStep === 1 && (
                <div className="job-role-creation-options">
                  <h3>{t("jobRoles.create.howToCreate")}</h3>

                  <div className="creation-options-container">
                    <div
                      className={`creation-option ${
                        createMode === "new" ? "selected" : ""
                      }`}
                      onClick={() => {
                        setCreateMode("new");
                        setNewRole((prev) => ({
                          ...prev,
                          createdFrom: "Scratch",
                          peopleUsedForCreation: [],
                        }));
                      }}
                    >
                      <div className="creation-option-icon">
                        <Briefcase size={32} />
                      </div>
                      <h4>{t("jobRoles.create.createNew")}</h4>
                      <p>{t("jobRoles.create.createNewDescription")}</p>
                    </div>

                    <div
                      className={`creation-option ${
                        createMode === "employee" ? "selected" : ""
                      }`}
                      onClick={() => {
                        setCreateMode("employee");
                        setNewRole((prev) => ({
                          ...prev,
                          createdFrom: "People",
                          peopleUsedForCreation: [],
                        }));
                      }}
                    >
                      <div className="creation-option-icon">
                        <Users size={32} />
                      </div>
                      <h4>{t("jobRoles.create.createFromEmployee")}</h4>
                      <p>
                        {t("jobRoles.create.createFromEmployeeDescription")}
                      </p>
                    </div>
                    {/* <div
                      className={`creation-option ${
                        createMode === "new" ? "selected" : ""
                      }`}
                      onClick={() => {
                        setShowAISuggestedRolesModal(true);
                      }}
                    > */}

                    <div
                      className={`creation-option ${
                        createMode === "ai" ? "selected" : ""
                      }`}
                      onClick={() => {
                        setCreateMode("ai");
                        setSelectedJobRoles([]);
                        setAiRoleDepartmentMap({});
                        setAiRoleDepartmentSearchMap({});
                        // suggestions fetched by useEffect(showCreateModal && createMode === 'ai')
                      }}
                    >
                      <div className="creation-option-icon">
                        {/* <Briefcase size={32} /> */}
                        <Brain size={32} />
                      </div>
                      <h4>{t("jobRoles.create.aiSuggestedJobRoles")}</h4>
                      <p>{t("jobRoles.create.aiSuggestedDescription")}</p>
                    </div>
                  </div>

                  {/* Employee Selection Section */}
                  {createMode === "employee" && (
                    <div className="employee-selection-container">
                      <h4>{t("jobRoles.create.selectEmployee")}</h4>
                      <div className="employee-search">
                        <Search size={16} className="employee-search-icon" />
                        <input
                          type="text"
                          placeholder={t("jobRoles.create.searchEmployees")}
                          value={employeeSearchTerm}
                          onChange={(e) =>
                            setEmployeeSearchTerm(e.target.value)
                          }
                          className="employee-search-input"
                        />
                      </div>

                      {selectedEmployees.length > 0 && (
                        <div style={{ marginBottom: "16px" }}>
                          <p
                            style={{
                              fontSize: "14px",
                              color: "var(--text-secondary)",
                              marginBottom: "8px",
                            }}
                          >
                            {t("jobRoles.selectedEmployees", {
                              count: selectedEmployees.length,
                            })}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            {selectedEmployees.map((emp) => (
                              <span key={emp.userId} className="skill-pill">
                                {emp.name}
                                <button
                                  type="button"
                                  onClick={() => handleSelectEmployee(emp)}
                                  className="skill-remove"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="employee-list">
                        {isLoadingEmployees ? (
                          <div className="no-employees">
                            {t("jobRoles.loadingEmployees")}
                          </div>
                        ) : filteredEmployees.length === 0 ? (
                          <div className="no-employees">
                            {t("jobRoles.create.noEmployeesFound")}
                          </div>
                        ) : (
                          filteredEmployees.map((employee) => (
                            <div
                              key={employee.userId}
                              className={`employee-item ${
                                selectedEmployees.some(
                                  (emp) => emp.userId === employee.userId,
                                )
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => handleSelectEmployee(employee)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedEmployees.some(
                                  (emp) => emp.userId === employee.userId,
                                )}
                                onChange={() => handleSelectEmployee(employee)}
                                className="role-checkbox"
                              />
                              <div className="employee-avatar">
                                {employee.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="employee-info">
                                <h5>{employee.name}</h5>
                                <p>
                                  {employee.department} • {employee.designation}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2 */}
              {createStep === 2 && createMode !== "ai" && (
                <div className="job-role-attributes-form">
                  <h3>{t("jobRoles.create.defineRequirements")}</h3>
                  <p className="form-subtitle">
                    {createMode === "employee" && isLoadingSkillValues
                      ? t("jobRoles.loadingSkillValues")
                      : t("jobRoles.create.setLevels")}
                  </p>

                  {isLoadingSkillValues ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <p>{t("jobRoles.fetchingSkillValues")}</p>
                    </div>
                  ) : (
                    <div className="attributes-grid">
                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.adaptability")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.adaptability}
                            onChange={(e) =>
                              handleAttributeChange(
                                "adaptability",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.adaptability,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.adaptabilityDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>
                          {t("jobRoles.attributes.attentionToDetail")}
                        </label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.attentionToDetail}
                            onChange={(e) =>
                              handleAttributeChange(
                                "attentionToDetail",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.attentionToDetail,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.attentionToDetailDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.communication")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.communication}
                            onChange={(e) =>
                              handleAttributeChange(
                                "communication",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.communication,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.communicationDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.creativity")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.creativity}
                            onChange={(e) =>
                              handleAttributeChange(
                                "creativity",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.creativity,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.creativityDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>
                          {t("jobRoles.attributes.interpersonalSkills")}
                        </label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.interpersonalSkills}
                            onChange={(e) =>
                              handleAttributeChange(
                                "interpersonalSkills",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.interpersonalSkills,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.interpersonalSkillsDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.leadership")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.leadership}
                            onChange={(e) =>
                              handleAttributeChange(
                                "leadership",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.leadership,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.leadershipDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.problemSolving")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.problemSolving}
                            onChange={(e) =>
                              handleAttributeChange(
                                "problemSolving",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.problemSolving,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.problemSolvingDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.teamwork")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.teamWork}
                            onChange={(e) =>
                              handleAttributeChange("teamWork", e.target.value)
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.teamWork,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.teamworkDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.timeManagement")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.timeManagement}
                            onChange={(e) =>
                              handleAttributeChange(
                                "timeManagement",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.timeManagement,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.timeManagementDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.workEthic")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.workEthic}
                            onChange={(e) =>
                              handleAttributeChange("workEthic", e.target.value)
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.workEthic,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.workEthicDesc")}
                        </p>
                      </div>
                      <div className="attribute-slider-group">
                        <label>
                          {t("jobRoles.attributes.professionalValue")}
                        </label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.professionValue}
                            onChange={(e) =>
                              handleAttributeChange(
                                "professionValue",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.professionValue,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.professionalValueDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.purposeValue")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.purposeValue}
                            onChange={(e) =>
                              handleAttributeChange(
                                "purposeValue",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.purposeValue,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.purposeValueDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.spiritValue")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.spiritValue}
                            onChange={(e) =>
                              handleAttributeChange(
                                "spiritValue",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.spiritValue,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.spiritValueDesc")}
                        </p>
                      </div>

                      <div className="attribute-slider-group">
                        <label>{t("jobRoles.attributes.rewardValue")}</label>
                        <div className="slider-container">
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={newRole.skillValues.rewardValue}
                            onChange={(e) =>
                              handleAttributeChange(
                                "rewardValue",
                                e.target.value,
                              )
                            }
                            className="attribute-slider"
                          />
                          <div className="slider-value">
                            {Number.parseFloat(
                              newRole.skillValues.rewardValue,
                            ).toFixed(1)}
                          </div>
                        </div>
                        <p className="attribute-description">
                          {t("jobRoles.attributes.rewardValueDesc")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2 */}
              {createStep === 2 && createMode === "ai" && (
                <div className="jr-ai-role-picker-shell">
                  {isLoading ? (
                    <div className="jr-ai-role-picker-loading">
                      <div className="onboarding-wizard-spinner"></div>
                      <p>{t("common.loading")}</p>
                    </div>
                  ) : (
                    <>
                      <div className="jr-ai-role-picker-header">
                        <div>
                          <h3>{t("jobRoles.aiSuggested.chooseTitle")}</h3>
                          <p>{t("jobRoles.aiSuggested.chooseDescription")}</p>
                        </div>

                        <div className="jr-ai-role-picker-count-card">
                          <strong>{selectedJobRoles.length}</strong>
                          <span>{t("jobRoles.aiSuggested.selected")}</span>
                        </div>
                      </div>

                      <div className="jr-ai-role-picker-grid">
                        {aiSuggestedRoles.map((jobRole, index) => {
                          const isSelected = selectedJobRoles.some(
                            (s) => s === jobRole,
                          );

                          return (
                            <button
                              key={index}
                              type="button"
                              className={`jr-ai-role-picker-card ${
                                isSelected
                                  ? "jr-ai-role-picker-card-selected"
                                  : ""
                              }`}
                              onClick={() => handleJobRoleSelection(jobRole)}
                            >
                              <div className="jr-ai-role-picker-card-icon">
                                <Briefcase size={18} />
                              </div>

                              <div className="jr-ai-role-picker-card-content">
                                <h4>{jobRole}</h4>
                                <p>
                                  {t("jobRoles.aiSuggested.aiSuggestedRole")}
                                </p>
                              </div>

                              {isSelected && (
                                <div className="jr-ai-role-picker-check">
                                  <Check size={15} />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      <div className="jr-ai-role-picker-bottom-bar">
                        <div>
                          <span className="jr-ai-role-picker-bottom-title">
                            {t("jobRoles.aiSuggested.rolesSelected", {
                              selected: selectedJobRoles.length,
                              total: aiSuggestedRoles.length,
                            })}
                          </span>
                          <p>{t("jobRoles.aiSuggested.selectAllNote")}</p>
                        </div>

                        <button
                          type="button"
                          className="jr-ai-role-picker-select-all-btn"
                          onClick={toggleSelectAllSuggestedRoles}
                          disabled={!aiSuggestedRoles.length}
                        >
                          {areAllSuggestedRolesSelected
                            ? t("jobRoles.aiSuggested.clearAll")
                            : t("jobRoles.aiSuggested.selectAll")}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {createStep === 3 && createMode === "ai" && (
                <div className="jr-ai-map-screen">
                  <div className="jr-ai-map-hero">
                    <div>
                      <span className="jr-ai-map-kicker">
                        {t("jobRoles.aiSuggested.departmentMapping")}
                      </span>
                      <h3>{t("jobRoles.aiSuggested.mapTitle")}</h3>
                      <p>{t("jobRoles.aiSuggested.mapDescription")}</p>
                    </div>

                    <div className="jr-ai-map-progress-card">
                      <strong>{selectedJobRoles.length}</strong>
                      <span>{t("jobRoles.aiSuggested.rolesReady")}</span>
                    </div>
                  </div>

                  <div className="jr-ai-map-layout">
                    <div className="jr-ai-map-role-list">
                      {selectedJobRoles.map((roleName, index) => {
                        const mappedDepartments =
                          aiRoleDepartmentMap[roleName] || [];

                        return (
                          <div key={roleName} className="jr-ai-map-role-card">
                            <div className="jr-ai-map-role-top">
                              <div className="jr-ai-map-role-index">
                                {index + 1}
                              </div>

                              <div className="jr-ai-map-role-title-wrap">
                                <h4>{roleName}</h4>
                                <p>
                                  {mappedDepartments.length > 0
                                    ? t(
                                        "jobRoles.aiSuggested.departmentsSelected",
                                        {
                                          count: mappedDepartments.length,
                                          plural:
                                            mappedDepartments.length > 1
                                              ? "s"
                                              : "",
                                        },
                                      )
                                    : t(
                                        "jobRoles.aiSuggested.selectAtLeastOneDepartment",
                                      )}
                                </p>
                              </div>
                            </div>

                            <div className="jr-ai-map-search-wrap">
                              <Search
                                size={15}
                                className="jr-ai-map-search-icon"
                              />
                              <input
                                type="text"
                                className="jr-ai-map-search-input"
                                placeholder={t(
                                  "jobRoles.aiSuggested.searchDepartments",
                                )}
                                value={
                                  aiRoleDepartmentSearchMap[roleName] || ""
                                }
                                onChange={(e) =>
                                  handleAiRoleDepartmentSearchChange(
                                    roleName,
                                    e.target.value,
                                  )
                                }
                              />

                              {(
                                aiRoleDepartmentSearchMap[roleName] || ""
                              ).trim() && (
                                <button
                                  type="button"
                                  className="jr-ai-map-search-clear"
                                  onClick={() =>
                                    handleAiRoleDepartmentSearchChange(
                                      roleName,
                                      "",
                                    )
                                  }
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>

                            <div className="jr-ai-map-selected-strip">
                              {mappedDepartments.length > 0 ? (
                                mappedDepartments.map((departmentName) => (
                                  <button
                                    key={departmentName}
                                    type="button"
                                    className="jr-ai-map-selected-mini-chip"
                                    onClick={() =>
                                      handleAiRoleDepartmentToggle(
                                        roleName,
                                        departmentName,
                                      )
                                    }
                                  >
                                    <span>{departmentName}</span>
                                    <X size={12} />
                                  </button>
                                ))
                              ) : (
                                <span className="jr-ai-map-selected-empty">
                                  {t(
                                    "jobRoles.aiSuggested.noDepartmentsSelectedYet",
                                  )}
                                </span>
                              )}
                            </div>

                            <div className="jr-ai-map-chip-scroll-area">
                              <div className="jr-ai-map-chip-grid">
                                {getFilteredAiRoleDepartments(roleName).length >
                                0 ? (
                                  getFilteredAiRoleDepartments(roleName).map(
                                    (department) => {
                                      const isMapped =
                                        mappedDepartments.includes(
                                          department.name,
                                        );

                                      return (
                                        <button
                                          key={department.id}
                                          type="button"
                                          className={`jr-ai-map-dept-chip ${
                                            isMapped
                                              ? "jr-ai-map-dept-chip-selected"
                                              : ""
                                          }`}
                                          onClick={() =>
                                            handleAiRoleDepartmentToggle(
                                              roleName,
                                              department.name,
                                            )
                                          }
                                        >
                                          <span>{department.name}</span>
                                          {isMapped && <Check size={14} />}
                                        </button>
                                      );
                                    },
                                  )
                                ) : (
                                  <div className="jr-ai-map-no-departments">
                                    {t(
                                      "jobRoles.aiSuggested.noDepartmentsMatchSearch",
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <aside className="jr-ai-map-summary-panel">
                      <div className="jr-ai-map-summary-header">
                        <h4>{t("jobRoles.aiSuggested.mappingSummary")}</h4>
                        <p>{t("jobRoles.aiSuggested.reviewCoverage")}</p>
                      </div>

                      <div className="jr-ai-map-summary-stat">
                        <span>{t("jobRoles.aiSuggested.mappedRoles")}</span>
                        <strong>
                          {
                            selectedJobRoles.filter(
                              (roleName) =>
                                (aiRoleDepartmentMap[roleName] || []).length >
                                0,
                            ).length
                          }
                          /{selectedJobRoles.length}
                        </strong>
                      </div>

                      <div className="jr-ai-map-summary-list">
                        {selectedJobRoles.map((roleName) => {
                          const mappedDepartments =
                            aiRoleDepartmentMap[roleName] || [];

                          return (
                            <div
                              key={roleName}
                              className="jr-ai-map-summary-item"
                            >
                              <span>{roleName}</span>
                              <small>
                                {mappedDepartments.length > 0
                                  ? mappedDepartments.join(", ")
                                  : t("jobRoles.aiSuggested.notMapped")}
                              </small>
                            </div>
                          );
                        })}
                      </div>

                      {!areAllSelectedAiRolesMapped && (
                        <div className="jr-ai-map-helper-note">
                          {t("jobRoles.aiSuggested.mapEverySelectedRole")}
                        </div>
                      )}
                    </aside>
                  </div>
                </div>
              )}

              {createStep === 3 && createMode !== "ai" && (
                <div className="job-role-details-form">
                  <h3>{t("jobRoles.create.jobRoleDetails")}</h3>
                  <p className="form-subtitle">
                    {t("jobRoles.create.provideInformation")}
                  </p>

                  <div className="form-group">
                    <label>
                      {t("jobRoles.create.functionDepartment")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      value={newRole.department}
                      onChange={(e) =>
                        handleInputChange("department", e.target.value)
                      }
                      required
                    >
                      <option value="">
                        {t("jobRoles.create.selectDepartment")}
                      </option>
                      {departments
                        .filter((d) => d.name !== "All")
                        .map((dept) => (
                          <option key={dept._id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                    </select>
                    {departmentError && (
                      <span className="input-error-message">
                        {departmentError}
                      </span>
                    )}
                  </div>

                  <div className="jr-hiring-status-field">
                    <label>
                      Role Availability
                      <JobRoleModalTooltip
                        tooltip={{
                          title: "Role Availability",
                          message:
                            "This does not affect employees already assigned to this role",
                        }}
                        id="create-role-availability-tooltip"
                      >
                        <span className="jr-inline-tooltip">
                          <Info size={13} />
                        </span>
                      </JobRoleModalTooltip>
                    </label>
                    <div className="jr-hiring-status-options">
                      {[
                        ROLE_AVAILABILITY.AVAILABLE,
                        ROLE_AVAILABILITY.CLOSED,
                      ].map((status) => (
                        <label key={status} className="jr-hiring-status-option">
                          <input
                            type="radio"
                            name="roleAvailability"
                            value={status}
                            checked={getRoleAvailability(newRole) === status}
                            onChange={(e) => {
                              const selectedStatus = e.target.value;
                              const availability =
                                selectedStatus === ROLE_AVAILABILITY.AVAILABLE;

                              setNewRole((prev) => ({
                                ...prev,
                                roleAvailability: selectedStatus,
                                hiringStatus: selectedStatus,
                                openForHiring: availability,
                                availability,
                                ...(availability
                                  ? {}
                                  : {
                                      roleAccess: ROLE_ACCESS.PRIVATE,
                                      selectedPartnerIds: [],
                                    }),
                              }));
                            }}
                          />
                          <span>{getRoleAvailabilityDisplayLabel(status)}</span>
                        </label>
                      ))}
                    </div>
                    <p className="jr-hiring-status-helper">
                      Choose whether this role should be available for hiring,
                      matching, and planning.
                    </p>
                  </div>

                  <div className="jr-role-access-field">
                    <label>
                      Role Type
                      {!canUsePartnerRoleSharing && (
                        <JobRoleModalTooltip
                          tooltip={{
                            title: "Public sharing unavailable",
                            message: hasPendingPartnerConnections
                              ? "Your partner connection is still pending. Public role type will be enabled after the partner connection becomes active."
                              : "No active partner with Job Role Sharing enabled is available.",
                          }}
                          id="create-role-public-disabled-tooltip"
                        >
                          <span className="jr-inline-tooltip">
                            <Info size={13} />
                          </span>
                        </JobRoleModalTooltip>
                      )}
                    </label>

                    {renderRoleAccessChooser({
                      name: "createRoleAccess",
                      value: newRole.roleAccess || ROLE_ACCESS.PRIVATE,
                      disabledAccesses:
                        !canUsePartnerRoleSharing ||
                        getRoleAvailability(newRole) ===
                          ROLE_AVAILABILITY.CLOSED
                          ? [ROLE_ACCESS.PUBLIC]
                          : [],
                      onChange: (access) =>
                        setNewRole((prev) => {
                          const previousPartnerIds = Array.isArray(
                            prev.selectedPartnerIds,
                          )
                            ? prev.selectedPartnerIds
                            : [];

                          return {
                            ...prev,
                            roleAccess: access,
                            selectedPartnerIds:
                              access === ROLE_ACCESS.PUBLIC
                                ? previousPartnerIds
                                : [],
                          };
                        }),
                    })}

                    <p className="jr-hiring-status-helper">
                      {getRoleAvailability(newRole) === ROLE_AVAILABILITY.CLOSED
                        ? "Closed roles cannot be shared with partners."
                        : newRole.roleAccess === ROLE_ACCESS.PUBLIC
                          ? "Public roles can be shared with selected eligible partners."
                          : "Private roles stay internal and visible only within your organization."}
                    </p>

                    {canUsePartnerRoleSharing &&
                      newRole.roleAccess === ROLE_ACCESS.PUBLIC &&
                      getRoleAvailability(newRole) !==
                        ROLE_AVAILABILITY.CLOSED &&
                      renderRolePartnerSearchSelect({
                        selectedIds: newRole.selectedPartnerIds,
                        searchValue: newRolePartnerSearch,
                        onSearchChange: setNewRolePartnerSearch,
                        onToggle: (partnerId, checked) =>
                          setNewRole((prev) => {
                            const previousPartnerIds = Array.isArray(
                              prev.selectedPartnerIds,
                            )
                              ? prev.selectedPartnerIds
                              : [];

                            return {
                              ...prev,
                              selectedPartnerIds: checked
                                ? Array.from(
                                    new Set([...previousPartnerIds, partnerId]),
                                  )
                                : previousPartnerIds.filter(
                                    (id) => id !== partnerId,
                                  ),
                            };
                          }),
                      })}
                  </div>
                  <div className="form-group">
                    <label>
                      {t("jobRoles.level")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      value={newRole.level}
                      onChange={(e) =>
                        handleInputChange("level", e.target.value)
                      }
                      required
                    >
                      {levels.map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </select>
                    {levelError && (
                      <span className="input-error-message">{levelError}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      {t("jobRoles.create.jobRoleName")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={newRole.roleName}
                      onChange={(e) =>
                        handleInputChange("roleName", e.target.value)
                      }
                      placeholder={t("jobRoles.create.enterJobRoleName")}
                      required
                    />
                    {roleNameError && (
                      <span className="input-error-message">
                        {roleNameError}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                      }}
                    >
                      <span>
                        {t("jobRoles.create.description")}{" "}
                        <span style={{ color: "red" }}>*</span>
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: 400,
                        }}
                      >
                        {newRole.shortDescription.length}/
                        {SHORT_DESCRIPTION_MAX_LENGTH}
                      </span>
                    </label>
                    <textarea
                      value={newRole.shortDescription}
                      onChange={(e) =>
                        handleInputChange("shortDescription", e.target.value)
                      }
                      placeholder={t("jobRoles.create.enterDescription")}
                      rows={3}
                      maxLength={SHORT_DESCRIPTION_MAX_LENGTH}
                      required
                      style={{ fontFamily: '"Titillium Web", sans-serif' }}
                    ></textarea>
                    {shortDescriptionError && (
                      <span className="input-error-message">
                        {shortDescriptionError}
                      </span>
                    )}
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "4px",
                      }}
                    >
                      {t("jobRoles.create.editNote")}
                    </p>
                  </div>

                  <div className="form-group job-role-long-description-group">
                    <label>Long Description</label>

                    <textarea
                      value={newRole.fullDescription}
                      onChange={(event) =>
                        handleInputChange("fullDescription", event.target.value)
                      }
                      placeholder="Enter the complete job role description"
                      rows={7}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="job-role-modal-footer">
              {createStep === 1 && (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowCreateModal(false)}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setCreateStep(2)}
                    disabled={
                      createMode === "" ||
                      (createMode === "employee" &&
                        selectedEmployees.length === 0)
                    }
                  >
                    {t("common.next")}
                  </button>
                </>
              )}

              {/* Step 1 — AI suggestions footer */}
              {createStep === 2 && createMode === "ai" && (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setCreateStep(1)}
                  >
                    {t("common.back")}
                  </button>
                  {/*<button
                    className="btn btn-primary"
                    disabled={isLoading || selectedJobRoles.length === 0}
                    onClick={() => setCreateStep(3)}
                  >
                    Next: map departments
                  </button>*/}
                  <button
                    className="btn btn-primary"
                    disabled={isLoading || selectedJobRoles.length === 0}
                    onClick={() => setCreateStep(3)}
                  >
                    {t("jobRoles.create.nextMapDepartments")}
                  </button>
                </>
              )}

              {createStep === 2 && createMode !== "ai" && (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setCreateStep(1)}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setCreateStep(3)}
                  >
                    {t("common.next")}
                  </button>
                </>
              )}

              {createStep === 3 && createMode === "ai" && (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setCreateStep(2)}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveAiGeneratedOrganizationRolesApiHandler}
                    disabled={isLoading || !areAllSelectedAiRolesMapped}
                  >
                    {isLoading
                      ? t("common.saving")
                      : t("jobRoles.create.saveRoles")}
                  </button>
                </>
              )}

              {/*{createStep === 3 && createMode !== "ai" && (
                <>
                  <button className="btn btn-outline" onClick={() => setCreateStep(2)}>
                    {t("common.back")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveAiGeneratedOrganizationRolesApiHandler}
                    disabled={isLoading || !areAllSelectedAiRolesMapped}
                  >
                    {isLoading ? "Saving..." : "Save roles"}
                  </button>
                </>
              )}*/}

              {createStep === 3 && createMode !== "ai" && (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setCreateStep(2)}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmitRole}
                    disabled={isLoading}
                  >
                    {isLoading
                      ? t("jobRoles.creating")
                      : t("jobRoles.create.createJobRole")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Job Role Modal */}
      {showEditModal && (
        <div className="job-role-modal-overlay">
          <div className="job-role-modal">
            <div className="job-role-modal-header">
              <h2>{t("jobRoles.create.editModalTitle")}</h2>
              <button
                className="job-role-modal-close"
                onClick={() => setShowEditModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="job-role-modal-content">
              <div className="job-role-details-form">
                <h3>{t("jobRoles.create.jobRoleDetails")}</h3>
                <p className="form-subtitle">{t("jobRoles.editJobRoleInfo")}</p>

                <div className="jr-hiring-status-field">
                  <label>
                    Role Availability
                    <JobRoleModalTooltip
                      tooltip={{
                        title: "Role Availability",
                        message:
                          "This does not affect employees already assigned to this role",
                      }}
                      id="edit-role-availability-tooltip"
                    >
                      <span className="jr-inline-tooltip">
                        <Info size={13} />
                      </span>
                    </JobRoleModalTooltip>
                  </label>
                  <div className="jr-hiring-status-options">
                    {[
                      ROLE_AVAILABILITY.AVAILABLE,
                      ROLE_AVAILABILITY.CLOSED,
                    ].map((status) => (
                      <label key={status} className="jr-hiring-status-option">
                        <input
                          type="radio"
                          name="editRoleAvailability"
                          value={status}
                          checked={getRoleAvailability(editRole) === status}
                          onChange={(event) => {
                            const selectedStatus = event.target.value;

                            const availability =
                              selectedStatus === ROLE_AVAILABILITY.AVAILABLE;

                            setEditRole((previousRole) => ({
                              ...previousRole,

                              // Actual Boolean field used by the API
                              availability,

                              // UI compatibility fields
                              openForHiring: availability,
                              hiringStatus: selectedStatus,
                              roleAvailability: selectedStatus,

                              ...(availability
                                ? {}
                                : {
                                    visibility: ROLE_ACCESS.PRIVATE,
                                    roleAccess: ROLE_ACCESS.PRIVATE,
                                    selectedPartnerIds: [],
                                  }),
                            }));
                          }}
                        />

                        <span>{getRoleAvailabilityDisplayLabel(status)}</span>
                      </label>
                    ))}
                  </div>
                  <p className="jr-hiring-status-helper">
                    Choose whether this role should be available for hiring,
                    matching, and planning.
                  </p>
                </div>

                <div className="jr-role-access-field">
                  <label>
                    Role Type
                    {!canUsePartnerRoleSharing && (
                      <JobRoleModalTooltip
                        tooltip={{
                          title: "Public sharing unavailable",
                          message: hasPendingPartnerConnections
                            ? "Your partner connection is still pending. Public role type will be enabled after the partner connection becomes active."
                            : "No active partner with Job Role Sharing enabled is available.",
                        }}
                        id="edit-role-public-disabled-tooltip"
                      >
                        <span className="jr-inline-tooltip">
                          <Info size={13} />
                        </span>
                      </JobRoleModalTooltip>
                    )}
                  </label>

                  {renderRoleAccessChooser({
                    name: "editRoleAccess",
                    value: editRole.roleAccess || ROLE_ACCESS.PRIVATE,
                    disabledAccesses:
                      !canUsePartnerRoleSharing ||
                      getRoleAvailability(editRole) === ROLE_AVAILABILITY.CLOSED
                        ? [ROLE_ACCESS.PUBLIC]
                        : [],
                    onChange: (access) =>
                      setEditRole((previousRole) => {
                        const previousPartnerIds = Array.isArray(
                          previousRole.selectedPartnerIds,
                        )
                          ? previousRole.selectedPartnerIds
                          : [];

                        return {
                          ...previousRole,
                          visibility: access,
                          roleAccess: access,

                          selectedPartnerIds:
                            access === ROLE_ACCESS.PUBLIC
                              ? previousPartnerIds
                              : [],
                        };
                      }),
                  })}

                  <p className="jr-hiring-status-helper">
                    {getRoleAvailability(editRole) === ROLE_AVAILABILITY.CLOSED
                      ? "Closed roles cannot be shared with partners."
                      : editRole.roleAccess === ROLE_ACCESS.PUBLIC
                        ? "Public roles can be shared with selected eligible partners."
                        : "Private roles stay internal and visible only within your organization."}
                  </p>

                  {canUsePartnerRoleSharing &&
                    editRole.roleAccess === ROLE_ACCESS.PUBLIC &&
                    getRoleAvailability(editRole) !==
                      ROLE_AVAILABILITY.CLOSED &&
                    renderRolePartnerSearchSelect({
                      selectedIds: editRole.selectedPartnerIds,
                      searchValue: editRolePartnerSearch,
                      onSearchChange: setEditRolePartnerSearch,
                      onToggle: (partnerId, checked) =>
                        setEditRole((prev) => {
                          const previousPartnerIds = Array.isArray(
                            prev.selectedPartnerIds,
                          )
                            ? prev.selectedPartnerIds
                            : [];

                          return {
                            ...prev,
                            selectedPartnerIds: checked
                              ? Array.from(
                                  new Set([...previousPartnerIds, partnerId]),
                                )
                              : previousPartnerIds.filter(
                                  (id) => id !== partnerId,
                                ),
                          };
                        }),
                    })}
                </div>
                <div className="form-group">
                  <label>{t("jobRoles.create.jobRoleName")}</label>
                  <input
                    type="text"
                    value={editRole.roleName}
                    onChange={(e) =>
                      handleEditInputChange("roleName", e.target.value)
                    }
                    placeholder={t("jobRoles.create.enterJobRoleName")}
                    required
                  />
                </div>

                <div className="form-group">
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}
                  >
                    <span>{t("jobRoles.shortDescription")}</span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        fontWeight: 400,
                      }}
                    >
                      {editRole.shortDescription.length}/
                      {SHORT_DESCRIPTION_MAX_LENGTH}
                    </span>
                  </label>
                  <textarea
                    value={editRole.shortDescription}
                    onChange={(e) =>
                      handleEditInputChange("shortDescription", e.target.value)
                    }
                    placeholder={t("jobRoles.enterBriefDescription")}
                    rows={3}
                    maxLength={SHORT_DESCRIPTION_MAX_LENGTH}
                    style={{ fontFamily: '"Titillium Web", sans-serif' }}
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>{t("jobRoles.create.functionDepartment")}</label>

                  <select
                    value={editRole.department}
                    onChange={(event) =>
                      handleEditInputChange("department", event.target.value)
                    }
                    required
                  >
                    <option value="">
                      {t("jobRoles.create.selectDepartment")}
                    </option>

                    {departments
                      .filter(
                        (department) =>
                          department._id !== "All" && department.name !== "All",
                      )
                      .map((department) => (
                        <option key={department._id} value={department.name}>
                          {department.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("jobRoles.level")}</label>

                  <select
                    value={editRole.level}
                    onChange={(event) =>
                      handleEditInputChange("level", event.target.value)
                    }
                    required
                  >
                    <option value="">{t("common.select")}</option>

                    {levels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group job-role-long-description-group">
                  <label>Long Description</label>
                  <textarea
                    value={editRole.fullDescription}
                    onChange={(e) =>
                      handleEditInputChange("fullDescription", e.target.value)
                    }
                    placeholder={t("jobRoles.enterDetailedDescription")}
                    rows={6}
                    style={{ fontFamily: '"Titillium Web", sans-serif' }}
                  ></textarea>
                  <button
                    type="button"
                    className="generate-description-btn"
                    onClick={generateEditJobDescription}
                    disabled={isGeneratingDescription}
                  >
                    <Sparkles size={16} />
                    {isGeneratingDescription
                      ? t("jobRoles.generating")
                      : t("jobRoles.create.generateDescription")}
                  </button>
                </div>

                <div className="form-group">
                  <label>{t("jobRoles.educationRequirements")}</label>
                  <div className="skills-container">
                    <div className="skills-pills">
                      {editRole.education.map((edu, index) => (
                        <span key={index} className="skill-pill">
                          {edu}
                          <button
                            type="button"
                            onClick={() => removeEducation(edu)}
                            className="skill-remove"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder={t("jobRoles.addEducationRequirements")}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          e.preventDefault();
                          const newEducation = e.target.value
                            .split(",")
                            .map((edu) => edu.trim())
                            .filter((edu) => edu);
                          setEditRole((prev) => ({
                            ...prev,
                            education: [
                              ...new Set([...prev.education, ...newEducation]),
                            ],
                          }));
                          e.target.value = "";
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t("jobRoles.skills")}</label>
                  <div className="skills-container">
                    <div className="skills-pills">
                      {editRole.hardSkills.map((skill, index) => (
                        <span key={index} className="skill-pill">
                          {skill}
                          <button
                            type="button"
                            onClick={() => removeSkill(skill)}
                            className="skill-remove"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      placeholder={t("jobRoles.addSkillsPlaceholder")}
                      onKeyPress={handleSkillInputKeyPress}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="job-role-modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowEditModal(false)}
              >
                {t("common.cancel")}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitEditRole}
                disabled={!editRole.roleName || isLoading}
              >
                {isLoading
                  ? t("jobRoles.updating")
                  : t("jobRoles.create.updateJobRole")}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkSharePartnerModalOpen && (
        <div
          className="mmh-share-partner-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="role-share-partner-title"
          onClick={() => {
            setBulkSharePartnerModalOpen(false);
            setBulkSharePartnerSearch("");
            setBulkSharePartnerSelection("");
            setShowBulkSharePartnerResults(false);
          }}
        >
          <div
            className="mmh-share-partner-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="mmh-share-partner-header">
              <div className="mmh-share-partner-heading">
                <span className="mmh-share-partner-heading-icon">
                  <Building2 size={20} />
                </span>

                <div>
                  <h2 id="role-share-partner-title">Share with Partner</h2>

                  <p>
                    Select one connected organization to receive the chosen job
                    roles.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="mmh-share-partner-close"
                aria-label="Close"
                onClick={() => {
                  setBulkSharePartnerModalOpen(false);
                  setBulkSharePartnerSearch("");
                  setBulkSharePartnerSelection("");
                  setShowBulkSharePartnerResults(false);
                }}
              >
                <X size={19} />
              </button>
            </header>

            <div className="mmh-share-partner-body">
              <div className="mmh-share-partner-summary">
                <span>
                  <Briefcase size={17} />
                </span>

                <div>
                  <strong>
                    {bulkSharePublicRoleCount} public job role
                    {bulkSharePublicRoleCount === 1 ? "" : "s"} selected
                  </strong>

                  <small>
                    {bulkSharePrivateRoleCount > 0
                      ? getBulkSharePrivateRoleMessage(
                          bulkSharePrivateRoleCount,
                        )
                      : "The selected organization will receive access according to its current sharing settings."}
                  </small>
                </div>
              </div>

              <label className="mmh-share-partner-field-label">
                Partner organization
              </label>

              <div
                className="mmh-share-partner-picker"
                ref={bulkSharePartnerPickerRef}
              >
                <div className="mmh-share-partner-control">
                  <Search size={17} />

                  <input
                    type="text"
                    value={bulkSharePartnerSearch}
                    placeholder="Search organization, code or admin"
                    onFocus={() => setShowBulkSharePartnerResults(true)}
                    onChange={(event) => {
                      const value = event.target.value;

                      setBulkSharePartnerSearch(value);
                      setShowBulkSharePartnerResults(true);

                      if (!value) {
                        setBulkSharePartnerSelection("");
                      }
                    }}
                    autoComplete="off"
                  />

                  {(bulkSharePartnerSearch || bulkSharePartnerSelection) && (
                    <button
                      type="button"
                      aria-label="Clear selected partner"
                      onClick={() => {
                        setBulkSharePartnerSearch("");
                        setBulkSharePartnerSelection("");
                        setShowBulkSharePartnerResults(true);
                      }}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>

                {showBulkSharePartnerResults && (
                  <div className="mmh-share-partner-results">
                    {filteredBulkSharePartnerOptions
                      .slice(0, 3)
                      .map((partner) => {
                        const isSelected =
                          bulkSharePartnerSelection === partner.id;

                        return (
                          <button
                            key={partner.id}
                            type="button"
                            className={`mmh-share-partner-option ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() => {
                              setBulkSharePartnerSelection(partner.id);
                              setBulkSharePartnerSearch(partner.name);
                              setShowBulkSharePartnerResults(false);
                            }}
                          >
                            <span className="mmh-share-partner-option-icon">
                              <Building2 size={17} />
                            </span>

                            <span className="mmh-share-partner-option-copy">
                              <strong>{partner.name}</strong>

                              <small>
                                {partner.orgCode}
                                {partner.admin
                                  ? ` \u00B7 ${partner.admin}`
                                  : ""}
                              </small>
                            </span>

                            {isSelected && (
                              <Check
                                size={17}
                                className="mmh-share-partner-option-check"
                              />
                            )}
                          </button>
                        );
                      })}

                    {!filteredBulkSharePartnerOptions.length && (
                      <div className="mmh-share-partner-empty">
                        <Search size={18} />
                        <strong>No matching organization</strong>
                        <span>
                          Try searching by organization name, code, admin, or
                          email.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedBulkRoleSharePartner && !showBulkSharePartnerResults && (
                <div className="mmh-share-partner-selected">
                  <span>
                    <Check size={16} />
                  </span>

                  <div>
                    <strong>{selectedBulkRoleSharePartner.name}</strong>

                    <small>
                      {selectedBulkRoleSharePartner.orgCode}
                      {selectedBulkRoleSharePartner.email
                        ? ` \u00B7 ${selectedBulkRoleSharePartner.email}`
                        : ""}
                    </small>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowBulkSharePartnerResults(true)}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>

            <footer className="mmh-share-partner-footer">
              <button
                type="button"
                className="mmh-share-partner-btn secondary"
                onClick={() => {
                  setBulkSharePartnerModalOpen(false);
                  setBulkSharePartnerSearch("");
                  setBulkSharePartnerSelection("");
                  setShowBulkSharePartnerResults(false);
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className="mmh-share-partner-btn primary"
                disabled={
                  !bulkSharePartnerSelection || bulkSharePublicRoleCount === 0
                }
                // onClick={() => {
                //   MessageManager.showToast(
                //     "success",
                //     "Job roles shared with partner.",
                //   );

                //   setBulkSharePartnerModalOpen(false);
                //   setBulkSharePartnerSearch("");
                //   setBulkSharePartnerSelection("");
                //   setShowBulkSharePartnerResults(false);
                //   setSelectedRoles([]);
                // }}

                onClick={handleBulkShareWithPartner}
              >
                Share Job Roles
              </button>
            </footer>
          </div>
        </div>
      )}

      {descriptionRole && (
        <div
          className="job-role-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setDescriptionRole(null);
            }
          }}
        >
          <div
            className="job-role-modal role-description-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-description-modal-title"
          >
            <div className="job-role-modal-header">
              <h2 id="role-description-modal-title">{descriptionRole.name}</h2>

              <button
                type="button"
                className="job-role-modal-close"
                onClick={() => setDescriptionRole(null)}
                aria-label="Close role description"
              >
                <X size={20} />
              </button>
            </div>
            <div className="job-role-modal-content role-description-modal-content">
              <p className="role-description-modal-text">
                {descriptionRole.fullDescription?.trim() ||
                  "No long description has been added for this role."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal.show}
        type="warning"
        title={t("jobRoles.delete.deleteJobRole")}
        message={t("jobRoles.delete.confirmDelete", {
          name:
            jobRoles.find((r) => r.id === showDeleteModal.roleId)?.name || "",
        })}
        onClose={() => setShowDeleteModal({ show: false, roleId: null })}
        onConfirm={confirmDeleteRole}
        showButton={true}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
      />

      {/* Enhanced Share Modal */}
      {showShareModal.show && (
        <div className="job-role-modal-overlay">
          <div className="job-role-modal share-modal">
            <div className="job-role-modal-header">
              <h2>
                <Sparkles size={24} />
                {t("jobRoles.shareJobRole")}
              </h2>
              <button
                className="job-role-modal-close"
                onClick={() => setShowShareModal({ show: false, role: null })}
              >
                <X size={20} />
              </button>
            </div>

            <div className="job-role-modal-content">
              <div className="share-role-info">
                <h3>{showShareModal.role?.name}</h3>
                <p>
                  <Briefcase size={16} />
                  {showShareModal.role?.department} •{" "}
                  {showShareModal.role?.level}
                </p>
              </div>

              <div className="share-options">
                <button
                  className="share-option whatsapp"
                  onClick={() =>
                    handleSharePlatform("whatsapp", showShareModal.role)
                  }
                >
                  <div className="share-option-icon">
                    <MessageCircle size={24} />
                  </div>
                  <span>{t("jobRoles.whatsapp")}</span>
                </button>

                <button
                  className="share-option linkedin"
                  onClick={() =>
                    handleSharePlatform("linkedin", showShareModal.role)
                  }
                >
                  <div className="share-option-icon">
                    <Linkedin size={24} />
                  </div>
                  <span>{t("jobRoles.linkedin")}</span>
                </button>

                <button
                  className="share-option email"
                  onClick={() =>
                    handleSharePlatform("email", showShareModal.role)
                  }
                >
                  <div className="share-option-icon">
                    <Mail size={24} />
                  </div>
                  <span>{t("jobRoles.email")}</span>
                </button>

                <button
                  className="share-option copy"
                  onClick={() =>
                    handleSharePlatform("copy", showShareModal.role)
                  }
                >
                  <div className="share-option-icon">
                    <Copy size={24} />
                  </div>
                  <span>{t("jobRoles.copyLink")}</span>
                </button>
              </div>

              <div className="share-url-container">
                <label>
                  <Link size={16} />
                  {t("jobRoles.shareUrl")}:
                </label>
                <div className="share-url-input">
                  <input
                    type="text"
                    value={
                      showShareModal.role
                        ? getShareUrl(showShareModal.role)
                        : ""
                    }
                    readOnly
                    className="share-url"
                  />
                  <button
                    className="copy-url-btn"
                    onClick={() =>
                      handleSharePlatform("copy", showShareModal.role)
                    }
                  >
                    <Copy size={16} />
                    {copyButtonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="job-role-modal-overlay">
          <div className="job-role-modal delete-modal">
            <div className="job-role-modal-header">
              <h2>{t("jobRoles.delete.deleteJobRoles")}</h2>
              <button
                className="job-role-modal-close"
                onClick={() => setShowBulkDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="job-role-modal-content">
              <p>
                {t("jobRoles.delete.confirmBulkDelete")} {selectedRoles.length}{" "}
                {t("jobRoles.delete.bulkDeleteSuffix")}
              </p>
            </div>

            <div className="job-role-modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowBulkDeleteModal(false)}
              >
                {t("common.cancel")}
              </button>
              <button
                disabled={!roleAccess.canDeleteRole}
                className="btn btn-outline"
                onClick={confirmBulkDelete}
              >
                {t("jobRoles.delete.deleteRoles")} {selectedRoles.length}{" "}
                {t("jobRoles.delete.roles")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={showCloseSharedConfirm.show}
        type="warning"
        title="Close shared role?"
        message="Changing this role to Closed will remove it from active partner sharing. Do you want to continue?"
        onClose={() => setShowCloseSharedConfirm({ show: false, mode: "" })}
        onConfirm={() => submitEditRoleToApi(true)}
        showButton={true}
        confirmText="Save"
        cancelText="Back"
      />

      {/* Success Notification Modal */}
      <SuccessNotificationModal
        show={showSuccessNotification.show}
        message={showSuccessNotification.message}
        onClose={() => setShowSuccessNotification({ show: false, message: "" })}
      />

      <Modal
        show={successModal.show}
        type="success"
        title={successModal.title}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, title: "", message: "" })}
        onConfirm={() =>
          setSuccessModal({ show: false, title: "", message: "" })
        }
        showButton={false}
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default JobRolesPage;

// ****************************************************************************
